import { useEffect, useState } from 'react'
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import {
  newId, makeButton, makeFeatureItem, makeGalleryItem, makeLogoItem,
  makeCard, makePricingTier, makeTimelineItem, makeArgMapping, makeFilter, makeHeroSlide,
} from './blocks'
import OnClickEditor from './actions/OnClickEditor'
import ColorValueRow from './ColorValueRow'
import { AssetPickerField } from './AssetPicker'
import ArgMappingEditor, { type ArgTargetOption } from './ArgMappingEditor'
import { useProjectVariables } from './useProjectVariables'
import ObjectPickerField from './ObjectPickerField'
import { reindexListBindings, moveOrder, removeOrder } from './listBindings'
import BackgroundExtrasEditor, {
  BACKGROUND_KIND_OPTIONS, backgroundForKind, isKnownBackgroundKind, type BackgroundExtrasSupport,
} from './BackgroundExtrasEditor'
import { buttonSpecOf } from './renderers'
import IconPicker from '@/components/common/IconPicker'
import { isVectorIcon } from '@twasta/page-runtime/iconMap'
import { FormDestinationEditor } from './FormDestinationEditor'
import { ECOM_ENDPOINT_OPTIONS, getEndpointDef } from '@/livepreview/ecommerce/endpoints'
import type {
  Align, Background, BackgroundKind, Block, ButtonSpec, ButtonVariant,
  ButtonSize, CardSpec, CalloutBlock, CalloutIconPosition, CalloutIconSize, CalloutVariant, ColumnsBlock,
  ColumnsCount, ContactFormBlock, ContactFormField, ContainerBlock,
  CtaBannerBlock, FaqBlock, FeatureGridBlock, GalleryBlock,
  HeroBlock, HeroSlide, HeroContentPosition, LogoCloudBlock, NewsletterBlock, PageModel, PricingBlock,
  SpacerBlock, StatsBlock, CountdownBlock, TabsBlock, TestimonialBlock, TestimonialItem, TimelineBlock, VideoBlock,
  AccordionBlock, ButtonBlock, ButtonGroupBlock, CardBlock, CardGridBlock,
  ImageBlock, MapBlock,
  EntityPickerBlock, NewCustomerFormBlock, ProductSelectionBlock,
  ProductPreviewBlock, OrderSummaryBlock, OrderSubmitBlock, PaymentCheckoutBlock,
  PaymentSettingsBlock,
  ArgMapping, EcomEndpointId, EcomSource, EcomSourceApi, EcomSourceTransaction,
  EmbedFilter, AuthMenuBlock, AuthMenuLink,
  CartBadgeBlock, CartLinesBlock, CartSummaryBlock,
  WishlistButtonBlock, WishlistBadgeBlock, WishlistLinesBlock, CardStyle, ShadowPreset,
  FileUploadBlock, FilterValueRef, PageInput,
} from './types'

// ─── Shared styles ───────────────────────────────────

export const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-secondary)',
  marginBottom: 4, display: 'block',
}
export const INPUT: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: '0.9125rem',
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 6, color: 'var(--color-text)', outline: 'none',
}
// Explanatory line under a field. Used where a wrong value is not merely
// untidy but changes what the application DOES — which columns money is read
// from, and who is allowed to pay.
export const HINT: React.CSSProperties = {
  margin: '4px 0 0', fontSize: 13, lineHeight: 1.5,
  color: 'var(--color-text-secondary)',
}
export const SUBSECTION: React.CSSProperties = {
  padding: 10, borderRadius: 8,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  display: 'flex', flexDirection: 'column', gap: 8,
}

export const TWO_COL: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }

// ─── Reusable subeditors ─────────────────────────────

// A select over a closed set of values that never misrepresents what is
// saved: an unset field shows the runtime default (`fallback`), and a saved
// value outside the set (an older/agent-authored model) is listed as-is
// instead of the browser silently showing the first option over it.
export function EnumSelect<T extends string>({ value, options, fallback, onChange, style }: {
  value: T | string | undefined
  options: ReadonlyArray<readonly [T, string]>
  fallback: T
  onChange: (v: T) => void
  style?: React.CSSProperties
}) {
  const current = value ?? fallback
  const known = options.some(([v]) => v === current)
  return (
    <select value={current} onChange={(e) => onChange(e.target.value as T)} style={{ ...INPUT, ...style }}>
      {!known && <option value={current}>{current} (unrecognised)</option>}
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  )
}

// Optional number: blank ⇒ undefined (the runtime default, shown as the
// placeholder) rather than a hard-coded value the author never chose.
export function OptionalNumber({ value, placeholder, min, max, step, onChange }: {
  value: number | undefined
  placeholder?: string
  min?: number
  max?: number
  step?: number | 'any'
  onChange: (n: number | undefined) => void
}) {
  return (
    <input
      type="number" min={min} max={max} step={step}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value
        if (raw.trim() === '') { onChange(undefined); return }
        const n = Number(raw)
        if (!Number.isFinite(n)) return
        onChange(n)
      }}
      style={INPUT}
    />
  )
}

// One-item-per-line list. Keeps the raw text while the author types and
// normalises (trim, drop blank lines) only on blur — normalising on every
// keystroke ate trailing spaces and made Enter unable to start a new line.
export function LinesTextarea({ value, onCommit, rows = 4, placeholder }: {
  value: string[]
  onCommit: (lines: string[]) => void
  rows?: number
  placeholder?: string
}) {
  const joined = (value || []).join('\n')
  const [draft, setDraft] = useState(joined)
  const [focused, setFocused] = useState(false)
  // Follow outside changes (undo, agent edits, another block) while idle.
  useEffect(() => { if (!focused) setDraft(joined) }, [joined, focused])
  return (
    <textarea
      rows={rows}
      value={draft}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false)
        const lines = draft.split('\n').map((l) => l.trim()).filter(Boolean)
        if (lines.join('\n') !== joined) onCommit(lines)
      }}
      style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }}
    />
  )
}

export const MEDIA_ASPECT_OPTIONS = [
  ['auto', 'Auto (natural)'], ['1:1', '1:1 square'], ['4:3', '4:3'], ['3:2', '3:2'],
  ['16:9', '16:9 wide'], ['3:4', '3:4 portrait'], ['2:3', '2:3 portrait'], ['21:9', '21:9 cinema'],
] as const
export const IMAGE_HOVER_OPTIONS = [
  ['none', 'None'], ['zoom', 'Zoom'], ['lift', 'Lift'], ['tilt', 'Tilt'],
  ['grayscale-off', 'Colour on hover'], ['reveal-caption', 'Reveal caption'],
] as const

export function NumberInput({ value, min, max, onChange }: {
  value: number; min?: number; max?: number; onChange: (n: number) => void
}) {
  return (
    <input
      type="number" min={min} max={max} value={value}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10)
        if (Number.isNaN(n)) return
        if (min !== undefined && n < min) return
        if (max !== undefined && n > max) return
        onChange(n)
      }}
      style={INPUT}
    />
  )
}

export function AlignPicker({ value, onChange }: { value: Align; onChange: (v: Align) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {(['left', 'center', 'right'] as Align[]).map((a) => (
        <button
          key={a}
          onClick={(e) => { e.stopPropagation(); onChange(a) }}
          style={{
            flex: 1, padding: '6px 0', fontSize: '0.8425rem',
            background: value === a ? 'var(--color-accent-weak)' : 'var(--color-bg)',
            color: value === a ? 'var(--color-accent)' : 'var(--color-text)',
            border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer',
            textTransform: 'capitalize',
          }}
        >{a}</button>
      ))}
    </div>
  )
}

export function ColumnsCountPicker({ value, max = 6, onChange }: { value: ColumnsCount; max?: number; onChange: (v: ColumnsCount) => void }) {
  const opts: ColumnsCount[] = [1, 2, 3, 4, 5, 6].slice(0, max) as ColumnsCount[]
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {opts.map((n) => (
        <button
          key={n}
          onClick={(e) => { e.stopPropagation(); onChange(n) }}
          style={{
            flex: 1, padding: '6px 0', fontSize: '0.8425rem',
            background: value === n ? 'var(--color-accent-weak)' : 'var(--color-bg)',
            color: value === n ? 'var(--color-accent)' : 'var(--color-text)',
            border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer',
          }}
        >{n}</button>
      ))}
    </div>
  )
}

// Which background layers a block's renderer actually paints. Hero, CTA
// banner and container draw the <video> layer and the drifting gradient
// (backgroundClass + VideoBackdrop); every other block goes through plain
// backgroundStyle, which shows a video's poster and a still gradient.
const BG_LIMITED_SUPPORT: BackgroundExtrasSupport = { videoLayer: false, drift: false }

export function BackgroundEditor({ value, project, onChange, support = BG_LIMITED_SUPPORT }: {
  value: Background | undefined
  project?: string
  onChange: (next: Background) => void
  support?: BackgroundExtrasSupport
}) {
  const bg: Background = value || { kind: 'none' }
  const isGradient = bg.kind === 'gradient' || bg.kind === 'gradient_animated'
  return (
    <div style={SUBSECTION}>
      <label style={FIELD_LABEL}>Background</label>
      <select
        value={bg.kind}
        // backgroundForKind keeps what still means something for the new
        // kind (an image's overlay on image ↔ video, stops across gradients).
        onChange={(e) => onChange(backgroundForKind(bg, e.target.value as BackgroundKind))}
        style={INPUT}
      >
        {!isKnownBackgroundKind(bg.kind) && <option value={bg.kind}>{bg.kind} (unrecognised)</option>}
        {BACKGROUND_KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {bg.kind === 'color' && (
        <div>
          <label style={FIELD_LABEL}>Color</label>
          <ColorWithPicker value={bg.color || '#0f172a'} onChange={(color) => onChange({ ...bg, color })} />
        </div>
      )}
      {isGradient && (
        <>
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>From</label>
              <ColorWithPicker value={bg.gradientFrom || '#0f172a'} onChange={(c) => onChange({ ...bg, gradientFrom: c })} />
            </div>
            <div>
              <label style={FIELD_LABEL}>To</label>
              <ColorWithPicker value={bg.gradientTo || '#1e3a8a'} onChange={(c) => onChange({ ...bg, gradientTo: c })} />
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>Angle ({bg.gradientAngle ?? 135}°)</label>
            <input
              type="range" min={0} max={360}
              value={bg.gradientAngle ?? 135}
              onChange={(e) => onChange({ ...bg, gradientAngle: parseInt(e.target.value, 10) })}
              style={{ width: '100%' }}
            />
          </div>
        </>
      )}
      {bg.kind === 'image' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Image</label>
            {project ? (
              <AssetPickerField project={project} value={bg.imageUrl} accept="image/*" onChange={(url) => onChange({ ...bg, imageUrl: url })} />
            ) : (
              <input value={bg.imageUrl || ''} onChange={(e) => onChange({ ...bg, imageUrl: e.target.value })} style={INPUT} />
            )}
          </div>
          <div>
            <label style={FIELD_LABEL}>Overlay (rgba)</label>
            <input
              value={bg.overlay || ''} placeholder="rgba(0,0,0,0.4)"
              onChange={(e) => onChange({ ...bg, overlay: e.target.value })}
              style={INPUT}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!bg.parallax}
              onChange={(e) => onChange({ ...bg, parallax: e.target.checked })}
            />
            Parallax on scroll
          </label>
          {bg.parallax && (
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Image stays fixed while the page scrolls over it (desktop; static on mobile).
            </span>
          )}
        </>
      )}
      {/* Video layer, gradient stops / radial shape, overlay gradient. The
          flat overlay input above stays the overlay control. */}
      <BackgroundExtrasEditor value={bg} project={project || ''} onChange={onChange} showOverlay={false} support={support} />
    </div>
  )
}

// An authored icon is a lucide NAME (what PageIcon renders as a vector) or
// a literal emoji. The picker covers the names; the text box beside it keeps
// an emoji — or a name the set doesn't know — visible and editable instead of
// the picker silently showing "Choose an icon" over a saved value.
export function IconField({ value, onChange, placeholder = 'Choose an icon' }: {
  value?: string
  onChange: (v: string | undefined) => void
  placeholder?: string
}) {
  const v = value || ''
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px auto', gap: 6, alignItems: 'center' }}>
      <IconPicker value={v} onChange={(n) => onChange(n)} placeholder={placeholder} />
      <input
        value={isVectorIcon(v) ? '' : v}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder="or emoji"
        title="An emoji, or any icon name"
        style={INPUT}
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(undefined) }}
        disabled={!v}
        className="btn btn-ghost btn-sm"
        style={{ padding: 2 }}
        title="No icon"
      >
        <Trash2 size={13} />
      </button>
    </div>
  )
}

function ColorWithPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // Delegates to the shared ColorValueRow so the inline editors get the
  // same Transparent + theme-default presets that BlockStyleEditor uses.
  return <ColorValueRow value={value} onChange={onChange} placeholder="#hex / var(--…) / transparent" />
}

export function ButtonSpecEditor({ value, onChange, onRemove, label }: {
  value: ButtonSpec
  onChange: (b: ButtonSpec) => void
  onRemove?: () => void
  label?: string
}) {
  return (
    <div style={SUBSECTION}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>{label || 'Button'}</label>
        {onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove() }} className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)', padding: 2 }}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <input value={value.label} onChange={(e) => onChange({ ...value, label: e.target.value })} placeholder="Label" style={INPUT} />
      <div style={TWO_COL}>
        <select value={value.variant} onChange={(e) => onChange({ ...value, variant: e.target.value as ButtonVariant })} style={INPUT}>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="outline">Outline</option>
          <option value="ghost">Ghost</option>
          <option value="link">Link</option>
        </select>
        <select value={value.size || 'md'} onChange={(e) => onChange({ ...value, size: e.target.value as ButtonSize })} style={INPUT}>
          <option value="sm">Small</option>
          <option value="md">Medium</option>
          <option value="lg">Large</option>
        </select>
      </div>
      <IconField value={value.icon} onChange={(icon) => onChange({ ...value, icon })} />

      {/* Click intent — replaces the bare `url` field. The legacy field is
          still read by PageButton when on_click is absent (back-compat). */}
      <OnClickEditor spec={value} onChange={onChange} />
    </div>
  )
}

export function OptionalButtonEditor({ value, onChange, label }: {
  value: ButtonSpec | undefined
  onChange: (b: ButtonSpec | undefined) => void
  label: string
}) {
  if (!value) {
    return (
      <button onClick={(e) => { e.stopPropagation(); onChange(makeButton(label, 'primary')) }} className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}>
        <Plus size={13} /> Add {label}
      </button>
    )
  }
  return <ButtonSpecEditor value={value} onChange={onChange} onRemove={() => onChange(undefined)} label={label} />
}

// ─── List item helpers (move/remove) ─────────────────

export function ItemControls({ onUp, onDown, onRemove, canUp, canDown }: {
  onUp?: () => void; onDown?: () => void; onRemove: () => void
  canUp: boolean; canDown: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {onUp && (
        <button onClick={(e) => { e.stopPropagation(); onUp() }} disabled={!canUp} className="btn btn-ghost btn-sm" style={{ padding: 2 }} title="Move up">
          <ArrowUp size={13} />
        </button>
      )}
      {onDown && (
        <button onClick={(e) => { e.stopPropagation(); onDown() }} disabled={!canDown} className="btn btn-ghost btn-sm" style={{ padding: 2 }} title="Move down">
          <ArrowDown size={13} />
        </button>
      )}
      <button onClick={(e) => { e.stopPropagation(); onRemove() }} className="btn btn-ghost btn-sm" style={{ padding: 2, color: 'var(--color-error)' }} title="Remove">
        <Trash2 size={13} />
      </button>
    </div>
  )
}

export function moveItem<T>(arr: T[], idx: number, dir: -1 | 1): T[] {
  const next = [...arr]
  const j = idx + dir
  if (j < 0 || j >= next.length) return arr
  const [item] = next.splice(idx, 1)
  next.splice(j, 0, item)
  return next
}

// ─── HERO ────────────────────────────────────────────

export function HeroEditor({ block, project, onChange }: { block: HeroBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<HeroBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Eyebrow</label>
        <input value={block.eyebrow || ''} onChange={(e) => upd({ eyebrow: e.target.value })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Title</label>
        <input value={block.title} onChange={(e) => upd({ title: e.target.value })} style={INPUT} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL}>Title size (px)</label>
          <input type="number" min={20} max={120} value={block.titleSize ?? ''} placeholder="auto"
            onChange={(e) => upd({ titleSize: e.target.value === '' ? undefined : Math.max(20, Number(e.target.value)) })} style={INPUT} />
        </div>
        <div>
          <label style={FIELD_LABEL}>Title weight</label>
          <select value={block.titleWeight ?? ''} style={INPUT}
            onChange={(e) => upd({ titleWeight: e.target.value === '' ? undefined : Number(e.target.value) })}>
            <option value="">Extra bold (800)</option>
            {[400, 500, 600, 700, 900].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>
      <p style={{ margin: '-4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
        The size applies on a wide screen; the headline scales down on smaller ones.
      </p>
      <div>
        <label style={FIELD_LABEL}>Subtitle</label>
        <textarea
          rows={2} value={block.subtitle || ''} onChange={(e) => upd({ subtitle: e.target.value })}
          style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align} onChange={(v) => upd({ align: v })} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Height</label>
        <EnumSelect value={block.height} fallback="normal"
          options={[['compact', 'Compact'], ['normal', 'Normal'], ['tall', 'Tall'], ['screen', 'Full screen']]}
          onChange={(v) => upd({ height: v })} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Text Color</label>
        <ColorWithPicker value={block.textColor || ''} onChange={(c) => upd({ textColor: c || undefined })} />
      </div>
      <BackgroundEditor value={block.background} project={project} support={{}} onChange={(bg) => upd({ background: bg })} />

      {/* ── Media carousel (images / video) ── */}
      <div style={SUBSECTION}>
        <label style={FIELD_LABEL}>Media carousel</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(block.slides || []).map((sl, i) => {
            const setSlide = (patch: Partial<HeroSlide>) => {
              const slides = [...(block.slides || [])]
              slides[i] = { ...sl, ...patch }
              upd({ slides })
            }
            return (
              <div key={sl.id} style={SUBSECTION}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Slide {i + 1}</label>
                  <ItemControls
                    onUp={() => upd({ slides: moveItem(block.slides || [], i, -1) })}
                    onDown={() => upd({ slides: moveItem(block.slides || [], i, 1) })}
                    onRemove={() => upd({ slides: (block.slides || []).filter((_, k) => k !== i) })}
                    canUp={i > 0} canDown={i < (block.slides || []).length - 1}
                  />
                </div>
                <select value={sl.type} onChange={(e) => setSlide({ type: e.target.value as HeroSlide['type'] })} style={INPUT}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
                {sl.type === 'image' ? (
                  <AssetPickerField project={project} value={sl.url} accept="image/*" onChange={(url) => setSlide({ url })} />
                ) : (
                  <>
                    <select value={sl.provider || 'file'} onChange={(e) => setSlide({ provider: e.target.value as HeroSlide['provider'] })} style={INPUT}>
                      <option value="file">File (mp4)</option>
                      <option value="youtube">YouTube</option>
                      <option value="vimeo">Vimeo</option>
                    </select>
                    <input value={sl.url} placeholder="Video URL" onChange={(e) => setSlide({ url: e.target.value })} style={INPUT} />
                  </>
                )}
              </div>
            )
          })}
          <button
            onClick={(e) => { e.stopPropagation(); upd({ slides: [...(block.slides || []), makeHeroSlide()] }) }}
            className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          ><Plus size={13} /> Add slide</button>
        </div>

        {block.slides && block.slides.length > 0 && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={block.autoplay !== false} onChange={(e) => upd({ autoplay: e.target.checked })} /> Autoplay
            </label>
            {block.autoplay !== false && (
              <div>
                <label style={FIELD_LABEL}>Autoplay interval (ms)</label>
                <NumberInput value={block.autoplayInterval ?? 5000} min={1500} max={20000} onChange={(v) => upd({ autoplayInterval: v })} />
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={block.showDots !== false} onChange={(e) => upd({ showDots: e.target.checked })} /> Show dots
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={block.showArrows !== false} onChange={(e) => upd({ showArrows: e.target.checked })} /> Show arrows
            </label>
            <div>
              <label style={FIELD_LABEL}>Content placement</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={block.contentPosition?.x || block.align}
                  onChange={(e) => upd({ contentPosition: { x: e.target.value as HeroContentPosition['x'], y: block.contentPosition?.y || 'center' } })}
                  style={INPUT}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
                <select
                  value={block.contentPosition?.y || 'center'}
                  onChange={(e) => upd({ contentPosition: { x: block.contentPosition?.x || block.align, y: e.target.value as HeroContentPosition['y'] } })}
                  style={INPUT}
                >
                  <option value="top">Top</option>
                  <option value="center">Middle</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
            </div>
            <div>
              <label style={FIELD_LABEL}>Scrim overlay (optional)</label>
              <input value={block.scrim || ''} placeholder="rgba(0,0,0,0.35)" onChange={(e) => upd({ scrim: e.target.value })} style={INPUT} />
            </div>
          </>
        )}
      </div>

      <div>
        <label style={FIELD_LABEL}>Side image position{block.slides && block.slides.length > 0 ? ' (ignored — carousel active)' : ''}</label>
        <select
          value={block.imageSide || 'none'}
          onChange={(e) => upd({ imageSide: e.target.value as HeroBlock['imageSide'] })}
          style={INPUT}
        >
          <option value="none">None</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
        </select>
      </div>
      {block.imageSide && block.imageSide !== 'none' && (
        <div>
          <label style={FIELD_LABEL}>Side image</label>
          <AssetPickerField project={project} value={block.imageUrl} accept="image/*" onChange={(url) => upd({ imageUrl: url })} />
        </div>
      )}
      <OptionalButtonEditor value={block.primaryCta} onChange={(b) => upd({ primaryCta: b })} label="Primary CTA" />
      <OptionalButtonEditor value={block.secondaryCta} onChange={(b) => upd({ secondaryCta: b })} label="Secondary CTA" />
    </>
  )
}

// ─── CTA Banner ──────────────────────────────────────

export function CtaBannerEditor({ block, project, onChange }: { block: CtaBannerBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CtaBannerBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Eyebrow</label>
        <input value={block.eyebrow || ''} onChange={(e) => upd({ eyebrow: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Title</label>
        <input value={block.title} onChange={(e) => upd({ title: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subtitle</label>
        <textarea rows={2} value={block.subtitle || ''} onChange={(e) => upd({ subtitle: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Text Color</label>
        <ColorWithPicker value={block.textColor || ''} onChange={(c) => upd({ textColor: c || undefined })} />
      </div>
      <BackgroundEditor value={block.background} project={project} support={{}} onChange={(bg) => upd({ background: bg })} />
      <OptionalButtonEditor value={block.primaryCta} onChange={(b) => upd({ primaryCta: b })} label="Primary CTA" />
      <OptionalButtonEditor value={block.secondaryCta} onChange={(b) => upd({ secondaryCta: b })} label="Secondary CTA" />
    </>
  )
}

// ─── Spacer ──────────────────────────────────────────

export function SpacerEditor({ block, onChange }: { block: SpacerBlock; onChange: (b: Block) => void }) {
  return (
    <div>
      <label style={FIELD_LABEL}>Height (px)</label>
      <NumberInput value={block.height} min={4} max={400} onChange={(h) => onChange({ ...block, height: h })} />
    </div>
  )
}

// ─── Columns ─────────────────────────────────────────

export function ColumnsEditor({ block, onChange }: { block: ColumnsBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ColumnsBlock>) => onChange({ ...block, ...patch })
  const setColCount = (count: ColumnsCount) => {
    const cols = [...block.columns]
    while (cols.length < count) cols.push({ id: newId('col'), text: '' })
    // Never truncate: every renderer shows `columns.slice(0, count)`, so
    // columns past the count are parked, not deleted — lowering the count
    // and raising it again brings their blocks back.
    upd({ count, columns: cols })
  }
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.count} max={6} onChange={setColCount} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Gap (px)</label>
        <NumberInput value={block.gap ?? 24} min={0} max={80} onChange={(g) => upd({ gap: g })} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Vertical alignment</label>
        <select value={block.align || 'top'} onChange={(e) => upd({ align: e.target.value as ColumnsBlock['align'] })} style={INPUT}>
          <option value="top">Top</option><option value="center">Center</option><option value="bottom">Bottom</option><option value="stretch">Stretch (equal height, blocks fill)</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Horizontal placement</label>
        <select value={block.justify || 'start'} onChange={(e) => upd({ justify: e.target.value as ColumnsBlock['justify'] })} style={INPUT}>
          <option value="start">Left</option><option value="center">Center</option>
          <option value="end">Right</option><option value="between">Space between</option>
        </select>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Applies only when every column has a fixed width (no fill column).
        </p>
      </div>
      <div>
        <label style={FIELD_LABEL}>On a phone</label>
        <select value={block.mobileLayout || 'stack'} onChange={(e) => upd({ mobileLayout: e.target.value === 'stack' ? undefined : e.target.value as ColumnsBlock['mobileLayout'] })} style={INPUT}>
          <option value="stack">Stack — one column per row</option>
          <option value="wrap">Keep side by side (wrap when full)</option>
        </select>
        {(block.mobileLayout || 'stack') === 'stack' && (
          <select value={block.mobileOrder || 'normal'} onChange={(e) => upd({ mobileOrder: e.target.value === 'normal' ? undefined : e.target.value as ColumnsBlock['mobileOrder'] })} style={{ ...INPUT, marginTop: 6 }}>
            <option value="normal">First column on top</option>
            <option value="reverse">Last column on top</option>
          </select>
        )}
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Keep side by side for a header tool bar, a logo lock-up or an icon row.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Column width: a fixed size like <code>180px</code> or <code>30%</code>.
          Leave blank to fill the remaining space (blank columns split it equally).
        </p>
        {block.columns.slice(0, block.count).map((col, i) => (
          <div key={col.id} style={SUBSECTION}>
            <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Column {i + 1}</label>
            <input
              type="text"
              value={col.width === undefined || col.width === null ? '' : String(col.width)}
              placeholder="Fill (e.g. 180px or 30%)"
              onChange={(e) => {
                const v = e.target.value.trim()
                const cols = [...block.columns]
                cols[i] = { ...cols[i], width: v || undefined }
                upd({ columns: cols })
              }}
              style={{ ...INPUT, marginBottom: 6 }}
            />
            <textarea
              rows={2} value={col.text}
              placeholder="Legacy text (optional)"
              onChange={(e) => {
                const cols = [...block.columns]
                cols[i] = { ...cols[i], text: e.target.value }
                upd({ columns: cols })
              }}
              style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        ))}
      </div>
    </>
  )
}

// ─── Container ───────────────────────────────────────

export function ContainerEditor({ block, project, onChange }: { block: ContainerBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ContainerBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Content</label>
        <textarea rows={4} value={block.text} onChange={(e) => upd({ text: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Padding (px)</label>
        <NumberInput value={block.padding ?? 24} min={0} max={80} onChange={(p) => upd({ padding: p })} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Text Color</label>
        <ColorWithPicker value={block.textColor || ''} onChange={(c) => upd({ textColor: c })} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.rounded} onChange={(e) => upd({ rounded: e.target.checked })} />
        Rounded corners
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label style={FIELD_LABEL}>Min height</label>
          <select value={block.minHeight === 'screen' ? 'screen' : block.minHeight ? 'px' : ''} style={INPUT}
            onChange={(e) => upd({ minHeight: e.target.value === 'screen' ? 'screen' : e.target.value === 'px' ? 400 : undefined })}>
            <option value="">Fit content</option>
            <option value="screen">Full screen height</option>
            <option value="px">Fixed (px)</option>
          </select>
          {typeof block.minHeight === 'number' && (
            <NumberInput value={block.minHeight} min={40} max={2000} onChange={(h) => upd({ minHeight: h })} />
          )}
        </div>
        <div><label style={FIELD_LABEL}>Content placement</label>
          <select value={block.justify || 'start'} style={INPUT}
            onChange={(e) => upd({ justify: e.target.value === 'start' ? undefined : e.target.value as ContainerBlock['justify'] })}>
            <option value="start">Top</option><option value="center">Middle</option>
            <option value="end">Bottom</option><option value="between">Spread (first top, last bottom)</option>
          </select>
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Max width (px)</label>
        <input type="number" min={120} value={block.maxWidth ?? ''} placeholder="full width" style={INPUT}
          onChange={(e) => upd({ maxWidth: e.target.value === '' ? undefined : Math.max(120, Number(e.target.value)) })} />
      </div>
      <BackgroundEditor value={block.background} project={project} support={{}} onChange={(bg) => upd({ background: bg })} />
    </>
  )
}

// ─── Card / CardSpec ─────────────────────────────────

/** Image fit + frame shape — shared by a single card and a grid's card style. */
export function CardImageLayoutFields({ fit, aspect, onChange, inheritLabel }: {
  fit: 'cover' | 'contain' | undefined
  aspect: string | undefined
  onChange: (patch: { imageFit?: 'cover' | 'contain'; imageAspect?: string }) => void
  /** Shown for the "unset" option — "Grid default" on a card in a grid. */
  inheritLabel?: string
}) {
  return (
    <div style={TWO_COL}>
      <div>
        <label style={FIELD_LABEL}>Image fit</label>
        <select value={fit || ''} style={INPUT}
          onChange={(e) => onChange({ imageFit: (e.target.value || undefined) as 'cover' | 'contain' | undefined })}>
          <option value="">{inheritLabel || 'Fill & crop'}</option>
          <option value="cover">Fill & crop</option>
          <option value="contain">Show whole image</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Image shape</label>
        <select value={aspect || ''} style={INPUT}
          onChange={(e) => onChange({ imageAspect: e.target.value || undefined })}>
          <option value="">{inheritLabel || 'Fixed height'}</option>
          <option value="1/1">Square</option>
          <option value="4/3">Landscape 4:3</option>
          <option value="3/4">Portrait 3:4</option>
          <option value="16/9">Wide 16:9</option>
        </select>
      </div>
    </div>
  )
}

export function CardSpecEditor({ value, project, onChange, label }: {
  value: CardSpec; project: string; onChange: (c: CardSpec) => void; label?: string
}) {
  return (
    <div style={SUBSECTION}>
      <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>{label || 'Card'}</label>
      <input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Title" style={INPUT} />
      <input value={value.subtitle || ''} onChange={(e) => onChange({ ...value, subtitle: e.target.value })} placeholder="Subtitle" style={INPUT} />
      <textarea rows={3} value={value.body || ''} onChange={(e) => onChange({ ...value, body: e.target.value })} placeholder="Body" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      <AssetPickerField project={project} value={value.imageUrl} accept="image/*" onChange={(url) => onChange({ ...value, imageUrl: url })} />
      <input value={value.imageAlt ?? ''} onChange={(e) => onChange({ ...value, imageAlt: e.target.value })} placeholder="Image alt text (defaults to the title)" style={INPUT} />
      <CardImageLayoutFields
        fit={value.imageFit} aspect={value.imageAspect}
        onChange={(patch) => onChange({ ...value, ...patch })}
      />
      <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Image on mouse-over (optional)</label>
      <AssetPickerField project={project} value={value.hoverImageUrl} accept="image/*"
        onChange={(url) => onChange({ ...value, hoverImageUrl: url || undefined })} />
      <p style={HINT}>Fades in while the pointer is over the card — e.g. the product's other angle. Bind it per row under "Bind fields to data".</p>
      <input value={value.badge || ''} onChange={(e) => onChange({ ...value, badge: e.target.value })} placeholder="Badge (optional)" style={INPUT} />
      <input value={value.eyebrow || ''} onChange={(e) => onChange({ ...value, eyebrow: e.target.value })} placeholder="Eyebrow (optional)" style={INPUT} />
      {/* The footer row. A price had nowhere to go before these, so it
          ended up inside `subtitle` — body-sized and indistinguishable
          from prose on a catalogue card. */}
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={value.price || ''} onChange={(e) => onChange({ ...value, price: e.target.value })} placeholder="Price" style={{ ...INPUT, flex: 2 }} />
        <input value={value.priceNote || ''} onChange={(e) => onChange({ ...value, priceNote: e.target.value })} placeholder="per unit" style={{ ...INPUT, flex: 3 }} />
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={value.compareAtPrice || ''} onChange={(e) => onChange({ ...value, compareAtPrice: e.target.value || undefined })}
               placeholder="Was price (MRP)" style={{ ...INPUT, flex: 2 }} />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, flex: 3 }}>
          <input type="checkbox" checked={!!value.showSavings}
                 onChange={(e) => onChange({ ...value, showSavings: e.target.checked || undefined })} />
          Show “N% off”
        </label>
      </div>
      {/* How the price renders. A price BOUND to a data source arrives as a
          bare number, so a catalogue needs 'currency' to read ₹3,200. */}
      <div style={{ display: 'flex', gap: 6 }}>
        <EnumSelect value={value.priceFormat} fallback="text" style={{ flex: 2 }}
          options={[['text', 'Price as typed'], ['currency', 'Currency'], ['number', 'Number']]}
          onChange={(v) => onChange({ ...value, priceFormat: v === 'text' ? undefined : v })} />
        {value.priceFormat === 'currency' && (
          <input value={value.priceCurrency || ''} placeholder="USD" title="ISO 4217 currency code"
                 onChange={(e) => onChange({ ...value, priceCurrency: e.target.value.trim().toUpperCase() || undefined })}
                 style={{ ...INPUT, flex: 1 }} />
        )}
        {(value.priceFormat === 'currency' || value.priceFormat === 'number') && (
          <input value={value.priceLocale || ''} title="Number grouping locale (BCP 47)"
                 placeholder={value.priceCurrency === 'INR' ? 'en-IN' : 'browser'}
                 onChange={(e) => onChange({ ...value, priceLocale: e.target.value.trim() || undefined })}
                 style={{ ...INPUT, flex: 1 }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={value.status?.label || ''}
          // Clearing the label keeps the tone: a label bound to a data
          // source has an empty static label but a tone the author chose.
          onChange={(e) => onChange({
            ...value,
            status: e.target.value || value.status?.tone
              ? { ...(value.status || {}), label: e.target.value }
              : undefined,
          })}
          placeholder="Status pill, e.g. In stock" style={{ ...INPUT, flex: 3 }}
        />
        <EnumSelect value={value.status?.tone} fallback="success" style={{ flex: 2 }}
          options={[['success', 'success'], ['neutral', 'neutral'], ['info', 'info'], ['warning', 'warning'], ['error', 'error']]}
          onChange={(v) => onChange({ ...value, status: { ...(value.status || { label: '' }), tone: v } })} />
      </div>
      <OptionalButtonEditor value={value.cta} onChange={(b) => onChange({ ...value, cta: b })} label="CTA" />
      <OptionalButtonEditor value={value.secondaryCta} onChange={(b) => onChange({ ...value, secondaryCta: b })} label="Second action" />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={!!value.wishlist}
               onChange={(e) => onChange({ ...value, wishlist: e.target.checked || undefined })} />
        Heart (save to wishlist)
      </label>
      {value.wishlist && (
        <>
          <input value={value.wishlistSku || ''} placeholder="Item code — default: the Add-to-cart code, else the title"
                 onChange={(e) => onChange({ ...value, wishlistSku: e.target.value || undefined })}
                 style={{ ...INPUT, fontFamily: 'monospace' }} />
          <p style={HINT}>Saves the same item the card's Add-to-cart button adds. Shows on the visitor's wishlist page (a Wishlist lines block).</p>
        </>
      )}
    </div>
  )
}

/** Card look shared by `card` and `card_grid`: corners, spacing, type sizes,
 *  outline and shadow. Placeholders are the renderer's defaults. */
export function CardLookFields({ value, onChange }: { value: CardStyle | undefined; onChange: (v: CardStyle | undefined) => void }) {
  const v = value || {}
  const set = (patch: Partial<CardStyle>) => {
    const next: CardStyle = { ...v, ...patch }
    for (const k of Object.keys(next) as (keyof CardStyle)[]) if (next[k] === undefined) delete next[k]
    onChange(Object.keys(next).length ? next : undefined)
  }
  const num = (key: 'radius' | 'padding' | 'titleSize' | 'bodySize', label: string, placeholder: string) => (
    <div><label style={FIELD_LABEL}>{label}</label>
      <input type="number" min={0} value={v[key] ?? ''} placeholder={placeholder}
             onChange={(e) => set({ [key]: optNumber(e.target.value) } as Partial<CardStyle>)} style={INPUT} />
    </div>
  )
  return (
    <>
      <div style={TWO_COL}>
        {num('radius', 'Corner radius (px)', '12')}
        {num('padding', 'Padding (px)', '16')}
      </div>
      <div style={TWO_COL}>
        {num('titleSize', 'Title size (px)', 'theme')}
        {num('bodySize', 'Body size (px)', 'theme')}
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Shadow</label>
          <select value={v.shadow || 'none'} style={INPUT}
                  onChange={(e) => set({ shadow: e.target.value === 'none' ? undefined : e.target.value as ShadowPreset })}>
            <option value="none">None</option>
            <option value="sm">Small</option>
            <option value="md">Medium</option>
            <option value="lg">Large</option>
            <option value="xl">Extra large</option>
          </select>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 18 }}>
          <input type="checkbox" checked={v.border !== false}
                 onChange={(e) => set({ border: e.target.checked ? undefined : false })} />
          Outline
        </label>
      </div>
    </>
  )
}

export function CardEditor({ block, project, onChange }: { block: CardBlock; project: string; onChange: (b: Block) => void }) {
  return (
    <>
      <CardSpecEditor value={block.card} project={project} onChange={(c) => onChange({ ...block, card: c })} />
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Card look</label>
        <CardLookFields value={block.cardStyle} onChange={(cardStyle) => onChange({ ...block, cardStyle })} />
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!block.fill}
                 onChange={(e) => onChange({ ...block, fill: e.target.checked || undefined })} />
          Fill the container (no 360px cap)
        </label>
        <p style={HINT}>Repeated in a grid, a card always fills its cell so every card in a row is the same height.</p>
      </div>
    </>
  )
}

export function CardGridEditor({ block, project, onChange }: { block: CardGridBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CardGridBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns} max={6} onChange={(n) => upd({ columns: n })} />
      </div>
      <div><label style={FIELD_LABEL}>Gap</label>
        <NumberInput value={block.gap ?? 16} min={0} max={64} onChange={(g) => upd({ gap: g })} />
      </div>
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Card images (all cards)</label>
        <CardImageLayoutFields
          fit={block.cardStyle?.imageFit} aspect={block.cardStyle?.imageAspect}
          onChange={(patch) => upd({ cardStyle: { ...(block.cardStyle || {}), ...patch } })}
        />
        <p style={HINT}>A card's own setting overrides these. "Show whole image" + "Square" keeps a product shot uncropped.</p>
      </div>
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Card look (all cards)</label>
        <CardLookFields value={block.cardStyle} onChange={(cardStyle) => upd({ cardStyle })} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.cards.map((c, i) => (
          <div key={c.id} style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', right: 4, top: 4, zIndex: 1 }}>
              <ItemControls
                onUp={() => upd({ cards: moveItem(block.cards, i, -1), bindings: reindexListBindings(block, 'cards', moveOrder(block.cards.length, i, -1)) })}
                onDown={() => upd({ cards: moveItem(block.cards, i, 1), bindings: reindexListBindings(block, 'cards', moveOrder(block.cards.length, i, 1)) })}
                onRemove={() => upd({ cards: block.cards.filter((_, k) => k !== i), bindings: reindexListBindings(block, 'cards', removeOrder(block.cards.length, i)) })}
                canUp={i > 0} canDown={i < block.cards.length - 1}
              />
            </div>
            <CardSpecEditor value={c} project={project} onChange={(nc) => {
              const next = [...block.cards]; next[i] = nc; upd({ cards: next })
            }} label={`Card ${i + 1}`} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ cards: [...block.cards, makeCard()] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add card</button>
      </div>
    </>
  )
}

// ─── Feature Grid ────────────────────────────────────

export function FeatureGridEditor({ block, onChange }: { block: FeatureGridBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<FeatureGridBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Eyebrow</label>
        <input value={block.eyebrow || ''} onChange={(e) => upd({ eyebrow: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subheading</label>
        <textarea rows={2} value={block.subheading || ''} onChange={(e) => upd({ subheading: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns} max={6} onChange={(n) => upd({ columns: n })} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Icon style</label>
        <select value={block.iconStyle || 'circle'} onChange={(e) => upd({ iconStyle: e.target.value as FeatureGridBlock['iconStyle'] })} style={INPUT}>
          <option value="circle">Circle</option><option value="square">Square</option><option value="plain">Plain</option>
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Feature {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <input value={it.icon || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, icon: e.target.value }; upd({ items })
            }} placeholder="Icon (emoji)" style={INPUT} />
            <input value={it.title} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, title: e.target.value }; upd({ items })
            }} placeholder="Title" style={INPUT} />
            <textarea rows={2} value={it.description || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, description: e.target.value }; upd({ items })
            }} placeholder="Description" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, makeFeatureItem()] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add feature</button>
      </div>
    </>
  )
}

// ─── Stats ───────────────────────────────────────────

export function StatsEditor({ block, project, onChange }: { block: StatsBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<StatsBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns} max={6} onChange={(n) => upd({ columns: n })} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(v) => upd({ align: v })} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.countUp} onChange={(e) => upd({ countUp: e.target.checked || undefined })} />
        Count up to each number when it scrolls into view
      </label>
      {(block.countUp || block.countUpDuration !== undefined) && (
        <div><label style={FIELD_LABEL}>Count-up duration (ms)</label>
          <OptionalNumber value={block.countUpDuration} min={100} step={100} placeholder="1600"
            onChange={(n) => upd({ countUpDuration: n })} />
        </div>
      )}
      <BackgroundEditor value={block.background} project={project} onChange={(bg) => upd({ background: bg })} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((s, i) => (
          <div key={s.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Stat {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <div style={TWO_COL}>
              <input value={s.value} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...s, value: e.target.value }; upd({ items })
              }} placeholder="Value" style={INPUT} />
              <input value={s.suffix || ''} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...s, suffix: e.target.value }; upd({ items })
              }} placeholder="Suffix" style={INPUT} />
            </div>
            <input value={s.label} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...s, label: e.target.value }; upd({ items })
            }} placeholder="Label" style={INPUT} />
            <input value={s.description || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...s, description: e.target.value }; upd({ items })
            }} placeholder="Description (optional)" style={INPUT} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, { id: newId('stat'), value: '0', label: 'Label' }] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add stat</button>
      </div>
    </>
  )
}

// ─── Countdown ───────────────────────────────────────

// <input type="datetime-local"> works in local time with no timezone, so
// we round-trip the stored absolute ISO target through the browser's zone:
// display the target as a local wall-clock value, and on edit store the
// picked instant back as a UTC ISO string.
function isoToLocalInput(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function localInputToIso(s: string): string {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

export function CountdownEditor({ block, project, onChange }: { block: CountdownBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CountdownBlock>) => onChange({ ...block, ...patch })
  const labels = block.labels || {}
  const updLabel = (patch: Partial<NonNullable<CountdownBlock['labels']>>) =>
    upd({ labels: { ...labels, ...patch } })
  const showSeconds = block.showSeconds !== false
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Target date &amp; time</label>
        <input
          type="datetime-local"
          value={isoToLocalInput(block.target)}
          onChange={(e) => upd({ target: localInputToIso(e.target.value) })}
          style={INPUT}
        />
        {/* The exact stored value. The picker can only write UTC to the
            minute, so a target with its own offset, seconds or a {{var}}
            template is kept — and edited — here. */}
        <input
          value={block.target || ''}
          onChange={(e) => upd({ target: e.target.value })}
          placeholder="2026-09-12T19:30:00+08:00 or {{event_start}}"
          style={{ ...INPUT, marginTop: 6, fontFamily: 'monospace', fontSize: '0.8425rem' }}
        />
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Counts down to this instant in every visitor's local time.
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns ?? (showSeconds ? 4 : 3)} max={4} onChange={(n) => upd({ columns: n })} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={showSeconds} onChange={(e) => upd({ showSeconds: e.target.checked })} />
        <span style={{ ...FIELD_LABEL, marginBottom: 0 }}>Show seconds</span>
      </label>
      <BackgroundEditor value={block.background} project={project} onChange={(bg) => upd({ background: bg })} />
      <div><label style={FIELD_LABEL}>Unit labels</label>
        <div style={TWO_COL}>
          <input value={labels.days ?? ''} onChange={(e) => updLabel({ days: e.target.value })} placeholder="Days" style={INPUT} />
          <input value={labels.hours ?? ''} onChange={(e) => updLabel({ hours: e.target.value })} placeholder="Hours" style={INPUT} />
          <input value={labels.minutes ?? ''} onChange={(e) => updLabel({ minutes: e.target.value })} placeholder="Minutes" style={INPUT} />
          <input value={labels.seconds ?? ''} onChange={(e) => updLabel({ seconds: e.target.value })} placeholder="Seconds" style={INPUT} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Text when finished</label>
        <input value={block.expiredText || ''} onChange={(e) => upd({ expiredText: e.target.value })} placeholder="It's here!" style={INPUT} />
      </div>
    </>
  )
}

// ─── Testimonial ─────────────────────────────────────

export function TestimonialEditor({ block, project, onChange }: { block: TestimonialBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<TestimonialBlock>) => onChange({ ...block, ...patch })
  const items = block.items || []
  const multi = items.length > 0
  // Mirrors the renderer: with no `mode`, 2+ quotes lay out as a wall.
  const mode = block.mode || (items.length > 1 ? 'wall' : 'single')
  const setItem = (i: number, patch: Partial<TestimonialItem>) => {
    const next = [...items]; next[i] = { ...items[i], ...patch }; upd({ items: next })
  }
  // The legacy single-quote fields stay the source while `items` is empty —
  // the first conversion seeds item 1 from them so nothing is retyped.
  const startItems = () => upd({
    items: [
      { id: newId('tq'), quote: block.quote || '', authorName: block.authorName || '', authorRole: block.authorRole,
        authorAvatarUrl: block.authorAvatarUrl, companyLogoUrl: block.companyLogoUrl, rating: block.rating },
      { id: newId('tq'), quote: '', authorName: '' },
    ],
  })
  const quoteFields = (v: TestimonialItem, set: (patch: Partial<TestimonialItem>) => void) => (
    <>
      <div><label style={FIELD_LABEL}>Quote</label>
        <textarea rows={4} value={v.quote || ''} onChange={(e) => set({ quote: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div style={TWO_COL}>
        <input value={v.authorName || ''} onChange={(e) => set({ authorName: e.target.value })} placeholder="Author name" style={INPUT} />
        <input value={v.authorRole || ''} onChange={(e) => set({ authorRole: e.target.value })} placeholder="Author role" style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Avatar</label>
        <AssetPickerField project={project} value={v.authorAvatarUrl} accept="image/*" onChange={(url) => set({ authorAvatarUrl: url })} />
      </div>
      <div><label style={FIELD_LABEL}>Company logo</label>
        <AssetPickerField project={project} value={v.companyLogoUrl} accept="image/*" onChange={(url) => set({ companyLogoUrl: url })} />
      </div>
      <div><label style={FIELD_LABEL}>Rating (0-5, blank = no stars)</label>
        <OptionalNumber value={v.rating} min={0} max={5} placeholder="No stars" onChange={(r) => set({ rating: r })} />
      </div>
    </>
  )
  return (
    <>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(v) => upd({ align: v })} />
      </div>
      {!multi ? (
        <>
          {quoteFields(
            { id: block.id, quote: block.quote, authorName: block.authorName, authorRole: block.authorRole,
              authorAvatarUrl: block.authorAvatarUrl, companyLogoUrl: block.companyLogoUrl, rating: block.rating },
            (patch) => upd(patch as Partial<TestimonialBlock>),
          )}
          <button onClick={(e) => { e.stopPropagation(); startItems() }} className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}>
            <Plus size={13} /> Add another quote (wall / carousel)
          </button>
        </>
      ) : (
        <>
          <div><label style={FIELD_LABEL}>Layout</label>
            <EnumSelect value={block.mode} fallback={mode}
              options={[['single', 'Single (first quote)'], ['wall', 'Wall of quotes'], ['carousel', 'Carousel']]}
              onChange={(v) => upd({ mode: v })} />
          </div>
          {mode === 'wall' && (
            <div><label style={FIELD_LABEL}>Columns</label>
              <ColumnsCountPicker value={block.columns ?? 3} max={6} onChange={(n) => upd({ columns: n })} />
            </div>
          )}
          {mode === 'carousel' && (
            <div style={TWO_COL}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={block.autoplay !== false} onChange={(e) => upd({ autoplay: e.target.checked })} />
                Autoplay
              </label>
              <OptionalNumber value={block.autoplayInterval} min={1000} step={500} placeholder="6000 ms"
                onChange={(n) => upd({ autoplayInterval: n })} />
            </div>
          )}
          {items.map((it, i) => (
            <div key={it.id} style={SUBSECTION}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Quote {i + 1}</label>
                <ItemControls
                  onUp={() => upd({ items: moveItem(items, i, -1) })}
                  onDown={() => upd({ items: moveItem(items, i, 1) })}
                  onRemove={() => upd({ items: items.filter((_, k) => k !== i) })}
                  canUp={i > 0} canDown={i < items.length - 1}
                />
              </div>
              {quoteFields(it, (patch) => setItem(i, patch))}
            </div>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); upd({ items: [...items, { id: newId('tq'), quote: '', authorName: '' }] }) }}
            className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          ><Plus size={13} /> Add quote</button>
          <p style={HINT}>With quotes listed here, the block&apos;s single-quote fields are not shown on the page.</p>
        </>
      )}
    </>
  )
}

// ─── Pricing ─────────────────────────────────────────

export function PricingEditor({ block, onChange }: { block: PricingBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<PricingBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subheading</label>
        <textarea rows={2} value={block.subheading || ''} onChange={(e) => upd({ subheading: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns} max={6} onChange={(n) => upd({ columns: n })} />
      </div>
      <div style={SUBSECTION}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!block.showPeriodToggle} onChange={(e) => upd({ showPeriodToggle: e.target.checked || undefined })} />
          Monthly / yearly switch
        </label>
        {block.showPeriodToggle && (
          <>
            <div style={TWO_COL}>
              <input value={block.periodLabel || ''} placeholder="Monthly" onChange={(e) => upd({ periodLabel: e.target.value || undefined })} style={INPUT} />
              <input value={block.altPeriodLabel || ''} placeholder="Yearly" onChange={(e) => upd({ altPeriodLabel: e.target.value || undefined })} style={INPUT} />
            </div>
            <input value={block.altPeriodBadge || ''} placeholder="Badge on the second option, e.g. Save 20%" onChange={(e) => upd({ altPeriodBadge: e.target.value || undefined })} style={INPUT} />
            <div><label style={FIELD_LABEL}>Shown first</label>
              <EnumSelect value={block.defaultPeriod} fallback="primary"
                options={[['primary', block.periodLabel || 'Monthly'], ['alt', block.altPeriodLabel || 'Yearly']]}
                onChange={(v) => upd({ defaultPeriod: v })} />
            </div>
            <p style={HINT}>Each tier&apos;s second price is set on the tier below.</p>
          </>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.tiers.map((t, i) => (
          <div key={t.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Tier {i + 1}</label>
              <ItemControls
                onUp={() => upd({ tiers: moveItem(block.tiers, i, -1) })}
                onDown={() => upd({ tiers: moveItem(block.tiers, i, 1) })}
                onRemove={() => upd({ tiers: block.tiers.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.tiers.length - 1}
              />
            </div>
            <input value={t.name} onChange={(e) => {
              const tiers = [...block.tiers]; tiers[i] = { ...t, name: e.target.value }; upd({ tiers })
            }} placeholder="Name" style={INPUT} />
            <div style={TWO_COL}>
              <input value={t.price} onChange={(e) => {
                const tiers = [...block.tiers]; tiers[i] = { ...t, price: e.target.value }; upd({ tiers })
              }} placeholder="Price" style={INPUT} />
              <input value={t.period || ''} onChange={(e) => {
                const tiers = [...block.tiers]; tiers[i] = { ...t, period: e.target.value }; upd({ tiers })
              }} placeholder="/mo" style={INPUT} />
            </div>
            {(block.showPeriodToggle || t.altPrice || t.altPeriod) && (
              <div style={TWO_COL}>
                <input value={t.altPrice || ''} onChange={(e) => {
                  const tiers = [...block.tiers]; tiers[i] = { ...t, altPrice: e.target.value || undefined }; upd({ tiers })
                }} placeholder={`${block.altPeriodLabel || 'Yearly'} price`} style={INPUT} />
                <input value={t.altPeriod || ''} onChange={(e) => {
                  const tiers = [...block.tiers]; tiers[i] = { ...t, altPeriod: e.target.value || undefined }; upd({ tiers })
                }} placeholder="/yr" style={INPUT} />
              </div>
            )}
            <input value={t.badge || ''} onChange={(e) => {
              const tiers = [...block.tiers]; tiers[i] = { ...t, badge: e.target.value }; upd({ tiers })
            }} placeholder="Badge (optional)" style={INPUT} />
            <textarea rows={2} value={t.description || ''} onChange={(e) => {
              const tiers = [...block.tiers]; tiers[i] = { ...t, description: e.target.value }; upd({ tiers })
            }} placeholder="Description" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
            <div>
              <label style={FIELD_LABEL}>Features (one per line)</label>
              <LinesTextarea
                rows={4}
                value={t.features}
                onCommit={(features) => {
                  const tiers = [...block.tiers]; tiers[i] = { ...t, features }; upd({ tiers })
                }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!t.highlighted} onChange={(e) => {
                const tiers = [...block.tiers]; tiers[i] = { ...t, highlighted: e.target.checked }; upd({ tiers })
              }} />
              Highlighted (most popular)
            </label>
            <OptionalButtonEditor
              value={t.cta}
              onChange={(b) => {
                const tiers = [...block.tiers]; tiers[i] = { ...t, cta: b }; upd({ tiers })
              }}
              label="CTA"
            />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ tiers: [...block.tiers, makePricingTier()] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add tier</button>
      </div>
    </>
  )
}

// ─── FAQ ─────────────────────────────────────────────

export function FaqEditor({ block, onChange }: { block: FaqBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<FaqBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Q&A {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <input value={it.question} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, question: e.target.value }; upd({ items })
            }} placeholder="Question" style={INPUT} />
            <textarea rows={3} value={it.answer} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, answer: e.target.value }; upd({ items })
            }} placeholder="Answer" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, { id: newId('faq'), question: 'Question', answer: 'Answer' }] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add Q&A</button>
      </div>
    </>
  )
}

// ─── Tabs ────────────────────────────────────────────

export function TabsEditor({ block, onChange }: { block: TabsBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<TabsBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Style</label>
        <select value={block.variant || 'underline'} onChange={(e) => upd({ variant: e.target.value as TabsBlock['variant'] })} style={INPUT}>
          <option value="underline">Underline</option>
          <option value="pill">Pill</option>
        </select>
      </div>
      <div><label style={FIELD_LABEL}>Overflow</label>
        <select value={block.overflow || 'wrap'} onChange={(e) => upd({ overflow: e.target.value as TabsBlock['overflow'] })} style={INPUT}>
          <option value="wrap">Wrap to next line</option>
          <option value="scroll">Single row (scroll)</option>
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Tab {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <input value={it.label} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, label: e.target.value }; upd({ items })
            }} placeholder="Tab label" style={INPUT} />
            {it.blocks?.length ? (
              <p style={HINT}>
                This tab shows its {it.blocks.length} block{it.blocks.length === 1 ? '' : 's'} — edit them on the canvas.
                {it.body ? ' Its old text body is no longer shown.' : ''}
              </p>
            ) : (
              <textarea rows={3} value={it.body || ''} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...it, body: e.target.value }; upd({ items })
              }} placeholder="Tab body" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
            )}
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, { id: newId('tab'), label: 'New tab', body: '' }] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add tab</button>
      </div>
    </>
  )
}

// ─── Accordion ───────────────────────────────────────

export function AccordionEditor({ block, onChange }: { block: AccordionBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<AccordionBlock>) => onChange({ ...block, ...patch })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {block.items.map((it, i) => (
        <div key={it.id} style={SUBSECTION}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Section {i + 1}</label>
            <ItemControls
              onUp={() => upd({ items: moveItem(block.items, i, -1) })}
              onDown={() => upd({ items: moveItem(block.items, i, 1) })}
              onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
              canUp={i > 0} canDown={i < block.items.length - 1}
            />
          </div>
          <input value={it.title} onChange={(e) => {
            const items = [...block.items]; items[i] = { ...it, title: e.target.value }; upd({ items })
          }} placeholder="Title" style={INPUT} />
          {it.blocks?.length ? (
            <p style={HINT}>
              This section shows its {it.blocks.length} block{it.blocks.length === 1 ? '' : 's'} — edit them on the canvas.
              {it.body ? ' Its old text body is no longer shown.' : ''}
            </p>
          ) : (
            <textarea rows={3} value={it.body || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, body: e.target.value }; upd({ items })
            }} placeholder="Body" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!it.defaultOpen} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, defaultOpen: e.target.checked }; upd({ items })
            }} />
            Open by default
          </label>
        </div>
      ))}
      <button
        onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, { id: newId('acc'), title: 'New section', body: '' }] }) }}
        className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
      ><Plus size={13} /> Add section</button>
    </div>
  )
}

// ─── Timeline ────────────────────────────────────────

export function TimelineEditor({ block, onChange }: { block: TimelineBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<TimelineBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Eyebrow</label>
        <input value={block.eyebrow || ''} onChange={(e) => upd({ eyebrow: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subheading</label>
        <textarea rows={2} value={block.subheading || ''} onChange={(e) => upd({ subheading: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Layout</label>
          <select value={block.layout || 'vertical'} onChange={(e) => upd({ layout: e.target.value as TimelineBlock['layout'] })} style={INPUT}>
            <option value="vertical">Single side</option>
            <option value="alternating">Alternating</option>
          </select>
        </div>
        <div><label style={FIELD_LABEL}>Marker style</label>
          <select value={block.markerStyle || 'dot'} onChange={(e) => upd({ markerStyle: e.target.value as TimelineBlock['markerStyle'] })} style={INPUT}>
            <option value="dot">Dot</option>
            <option value="icon">Icon</option>
            <option value="number">Number</option>
          </select>
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Header alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Accent color</label>
        <ColorValueRow
          value={block.accentColor || ''}
          onChange={(v) => upd({ accentColor: v || undefined })}
          placeholder="var(--pg-primary)"
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Milestone {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <div style={TWO_COL}>
              <input value={it.date || ''} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...it, date: e.target.value }; upd({ items })
              }} placeholder="Date / label" style={INPUT} />
              <input value={it.icon || ''} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...it, icon: e.target.value }; upd({ items })
              }} placeholder="Icon (emoji)" style={INPUT} />
            </div>
            <input value={it.title} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, title: e.target.value }; upd({ items })
            }} placeholder="Title" style={INPUT} />
            <textarea rows={2} value={it.description || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...it, description: e.target.value }; upd({ items })
            }} placeholder="Description" style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={TWO_COL}>
              <input value={it.badge || ''} onChange={(e) => {
                const items = [...block.items]; items[i] = { ...it, badge: e.target.value }; upd({ items })
              }} placeholder="Badge (optional)" style={INPUT} />
              <ColorValueRow
                value={it.accent || ''}
                onChange={(v) => {
                  const items = [...block.items]; items[i] = { ...it, accent: v || undefined }; upd({ items })
                }}
                placeholder="Accent override"
              />
            </div>
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, makeTimelineItem('', 'New milestone', '', '✨')] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add milestone</button>
      </div>
    </>
  )
}

// ─── Logo cloud ──────────────────────────────────────

export function LogoCloudEditor({ block, project, onChange }: { block: LogoCloudBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<LogoCloudBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Title</label>
        <input value={block.title || ''} onChange={(e) => upd({ title: e.target.value })} style={INPUT} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.monochrome} onChange={(e) => upd({ monochrome: e.target.checked })} />
        Monochrome (grayscale)
      </label>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Display</label>
          <EnumSelect value={block.mode} fallback="row"
            options={[['row', 'Static row'], ['marquee', 'Scrolling marquee']]}
            onChange={(v) => upd({ mode: v })} />
        </div>
        <div><label style={FIELD_LABEL}>Logo height (px)</label>
          <OptionalNumber value={block.logoHeight} min={8} placeholder="32" onChange={(n) => upd({ logoHeight: n })} />
        </div>
      </div>
      {(block.mode === 'marquee' || block.marqueeSpeed !== undefined || block.marqueeDirection) && (
        <div style={TWO_COL}>
          <div><label style={FIELD_LABEL}>Speed (s per pass)</label>
            <OptionalNumber value={block.marqueeSpeed} min={1} placeholder="30" onChange={(n) => upd({ marqueeSpeed: n })} />
          </div>
          <div><label style={FIELD_LABEL}>Direction</label>
            <EnumSelect value={block.marqueeDirection} fallback="left"
              options={[['left', 'Left'], ['right', 'Right']]}
              onChange={(v) => upd({ marqueeDirection: v })} />
          </div>
        </div>
      )}
      <BackgroundEditor value={block.background} project={project} onChange={(bg) => upd({ background: bg })} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((lg, i) => (
          <div key={lg.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Logo {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <input value={lg.name || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...lg, name: e.target.value }; upd({ items })
            }} placeholder="Brand name" style={INPUT} />
            <AssetPickerField project={project} value={lg.url} accept="image/*" onChange={(url) => {
              const items = [...block.items]; items[i] = { ...lg, url }; upd({ items })
            }} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, makeLogoItem()] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add logo</button>
      </div>
    </>
  )
}

// ─── Image (enhanced editor) ─────────────────────────

export function ImageEditor({ block, project, onChange }: { block: ImageBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ImageBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Image</label>
        <AssetPickerField project={project} value={block.url} accept="image/*" onChange={(url) => upd({ url })} />
      </div>
      <div><label style={FIELD_LABEL}>Alt Text</label>
        <input value={block.alt || ''} onChange={(e) => upd({ alt: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Caption</label>
        <input value={block.caption || ''} onChange={(e) => upd({ caption: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Max width (px, blank = full)</label>
        <input
          type="number" min={50}
          value={block.maxWidth ?? ''}
          onChange={(e) => {
            const v = e.target.value
            upd({ maxWidth: v ? parseInt(v, 10) : undefined })
          }}
          style={INPUT}
        />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.rounded} onChange={(e) => upd({ rounded: e.target.checked })} />
        Rounded
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.shadow} onChange={(e) => upd({ shadow: e.target.checked })} />
        Drop shadow
      </label>
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Framing</label>
        <div style={TWO_COL}>
          <div><label style={FIELD_LABEL}>Aspect</label>
            <EnumSelect value={block.aspect} options={MEDIA_ASPECT_OPTIONS} fallback="auto"
              onChange={(v) => upd({ aspect: v === 'auto' ? undefined : v })} />
          </div>
          <div><label style={FIELD_LABEL}>Fit</label>
            <EnumSelect value={block.objectFit} fallback="cover"
              options={[['cover', 'Cover (crop)'], ['contain', 'Contain'], ['fill', 'Fill'], ['none', 'None'], ['scale-down', 'Scale down']]}
              onChange={(v) => upd({ objectFit: v })} />
          </div>
        </div>
        <div><label style={FIELD_LABEL}>Focal point</label>
          <input value={block.objectPosition || ''} placeholder="center · top left · 50% 20%"
                 onChange={(e) => upd({ objectPosition: e.target.value || undefined })} style={INPUT} />
          <p style={HINT}>Which part stays in view when the aspect crops the image.</p>
        </div>
        <div style={TWO_COL}>
          <div><label style={FIELD_LABEL}>Shape</label>
            <EnumSelect value={block.mask} fallback="none"
              options={[['none', 'None'], ['circle', 'Circle'], ['squircle', 'Squircle'], ['blob', 'Blob'], ['arch', 'Arch'], ['diamond', 'Diamond'], ['hexagon', 'Hexagon']]}
              onChange={(v) => upd({ mask: v === 'none' ? undefined : v })} />
          </div>
          <div><label style={FIELD_LABEL}>Hover</label>
            <EnumSelect value={block.hoverEffect} options={IMAGE_HOVER_OPTIONS} fallback="none"
              onChange={(v) => upd({ hoverEffect: v === 'none' ? undefined : v })} />
          </div>
        </div>
        <div style={TWO_COL}>
          <div><label style={FIELD_LABEL}>Width (px)</label>
            <OptionalNumber value={block.naturalWidth} min={1} placeholder="unknown" onChange={(n) => upd({ naturalWidth: n })} />
          </div>
          <div><label style={FIELD_LABEL}>Height (px)</label>
            <OptionalNumber value={block.naturalHeight} min={1} placeholder="unknown" onChange={(n) => upd({ naturalHeight: n })} />
          </div>
        </div>
        <p style={HINT}>The image&apos;s own pixel size, so the browser reserves its box before it loads.</p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!block.priority} onChange={(e) => upd({ priority: e.target.checked || undefined })} />
          Above the fold — load immediately instead of lazily
        </label>
      </div>
    </>
  )
}

// ─── Video ───────────────────────────────────────────

export function VideoEditor({ block, project, onChange }: { block: VideoBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<VideoBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Provider</label>
        <select value={block.provider} onChange={(e) => upd({ provider: e.target.value as VideoBlock['provider'] })} style={INPUT}>
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
          <option value="file">Direct file URL</option>
        </select>
      </div>
      <div><label style={FIELD_LABEL}>URL</label>
        {block.provider === 'file' ? (
          // Self-hosted file → offer the library / upload. YouTube & Vimeo
          // are external embeds, so they stay a plain URL field.
          <AssetPickerField project={project} value={block.url} accept="video/*" onChange={(url) => upd({ url })} />
        ) : (
          <input value={block.url} onChange={(e) => upd({ url: e.target.value })} style={INPUT} placeholder="Video URL" />
        )}
      </div>
      <div><label style={FIELD_LABEL}>Aspect Ratio</label>
        <select value={block.aspect || '16:9'} onChange={(e) => upd({ aspect: e.target.value as VideoBlock['aspect'] })} style={INPUT}>
          <option value="16:9">16:9</option><option value="4:3">4:3</option><option value="1:1">1:1</option>
        </select>
      </div>
      <div><label style={FIELD_LABEL}>Caption</label>
        <input value={block.caption || ''} onChange={(e) => upd({ caption: e.target.value })} style={INPUT} />
      </div>
    </>
  )
}

// ─── Map ─────────────────────────────────────────────

export function MapEditor({ block, onChange }: { block: MapBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<MapBlock>) => onChange({ ...block, ...patch })
  const needsKey = block.provider === 'google' || block.provider === 'mapbox'
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Provider</label>
        <select
          value={block.provider}
          onChange={(e) => upd({ provider: e.target.value as MapBlock['provider'] })}
          style={INPUT}
        >
          <option value="leaflet">Leaflet (OpenStreetMap, keyless)</option>
          <option value="google">Google Maps</option>
          <option value="mapbox">Mapbox</option>
        </select>
      </div>

      {needsKey && (
        <div>
          <label style={FIELD_LABEL}>
            {block.provider === 'google' ? 'Google Maps API key' : 'Mapbox access token'}
          </label>
          <input
            value={block.apiKey || ''}
            onChange={(e) => upd({ apiKey: e.target.value })}
            style={INPUT}
            placeholder={block.provider === 'google' ? 'AIza…' : 'pk.eyJ…'}
            type="password"
            autoComplete="off"
          />
        </div>
      )}

      <div>
        <label style={FIELD_LABEL}>Location</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={block.locationMode === 'address'}
              onChange={() => upd({ locationMode: 'address' })}
            />
            Address
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={block.locationMode === 'coordinates'}
              onChange={() => upd({ locationMode: 'coordinates' })}
            />
            Coordinates
          </label>
        </div>

        {block.locationMode === 'address' ? (
          <input
            value={block.address || ''}
            onChange={(e) => upd({ address: e.target.value })}
            style={INPUT}
            placeholder="e.g. 1600 Amphitheatre Pkwy, Mountain View, CA"
          />
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              step="any"
              value={block.latitude ?? ''}
              onChange={(e) => upd({ latitude: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
              style={{ ...INPUT, flex: 1 }}
              placeholder="Latitude"
            />
            <input
              type="number"
              step="any"
              value={block.longitude ?? ''}
              onChange={(e) => upd({ longitude: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
              style={{ ...INPUT, flex: 1 }}
              placeholder="Longitude"
            />
          </div>
        )}
      </div>

      <div>
        <label style={FIELD_LABEL}>Zoom (1–20)</label>
        <input
          type="number"
          min={1}
          max={20}
          value={block.zoom ?? 14}
          onChange={(e) => upd({ zoom: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
          style={INPUT}
        />
      </div>

      <div>
        <label style={FIELD_LABEL}>Height (px)</label>
        <input
          type="number"
          min={120}
          value={block.height ?? 400}
          onChange={(e) => upd({ height: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
          style={INPUT}
        />
      </div>

      <div>
        <label style={FIELD_LABEL}>Marker title</label>
        <input
          value={block.title || ''}
          onChange={(e) => upd({ title: e.target.value })}
          style={INPUT}
          placeholder="Optional"
        />
      </div>

      <div>
        <label style={FIELD_LABEL}>Caption</label>
        <input
          value={block.caption || ''}
          onChange={(e) => upd({ caption: e.target.value })}
          style={INPUT}
        />
      </div>
    </>
  )
}

// ─── Gallery ─────────────────────────────────────────

export function GalleryEditor({ block, project, onChange }: { block: GalleryBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<GalleryBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns} max={6} onChange={(n) => upd({ columns: n })} />
      </div>
      <div><label style={FIELD_LABEL}>Gap</label>
        <NumberInput value={block.gap ?? 12} min={0} max={48} onChange={(g) => upd({ gap: g })} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.rounded} onChange={(e) => upd({ rounded: e.target.checked })} />
        Rounded thumbnails
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!block.lightbox} onChange={(e) => upd({ lightbox: e.target.checked || undefined })} />
        Open full-size on click (lightbox)
      </label>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Layout</label>
          <EnumSelect value={block.layout} fallback="grid"
            options={[['grid', 'Grid (equal tiles)'], ['masonry', 'Masonry']]}
            onChange={(v) => upd({ layout: v })} />
        </div>
        <div><label style={FIELD_LABEL}>Hover</label>
          <EnumSelect value={block.hoverEffect} options={IMAGE_HOVER_OPTIONS} fallback="none"
            onChange={(v) => upd({ hoverEffect: v === 'none' ? undefined : v })} />
        </div>
      </div>
      {(block.layout !== 'masonry' || block.aspect) && (
        <div><label style={FIELD_LABEL}>Tile aspect{block.layout === 'masonry' ? ' (ignored in masonry)' : ''}</label>
          <EnumSelect value={block.aspect} options={MEDIA_ASPECT_OPTIONS} fallback="auto"
            onChange={(v) => upd({ aspect: v === 'auto' ? undefined : v })} />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.items.map((im, i) => (
          <div key={im.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Image {i + 1}</label>
              <ItemControls
                onUp={() => upd({ items: moveItem(block.items, i, -1) })}
                onDown={() => upd({ items: moveItem(block.items, i, 1) })}
                onRemove={() => upd({ items: block.items.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.items.length - 1}
              />
            </div>
            <AssetPickerField project={project} value={im.url} accept="image/*" onChange={(url) => {
              const items = [...block.items]; items[i] = { ...im, url }; upd({ items })
            }} />
            <input value={im.caption || ''} onChange={(e) => {
              const items = [...block.items]; items[i] = { ...im, caption: e.target.value }; upd({ items })
            }} placeholder="Caption (optional)" style={INPUT} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ items: [...block.items, makeGalleryItem()] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add image</button>
      </div>
    </>
  )
}

// ─── Button / Button group ───────────────────────────

export function ButtonEditor({ block, onChange }: { block: ButtonBlock; onChange: (b: Block) => void }) {
  return (
    <>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align} onChange={(v) => onChange({ ...block, align: v })} />
      </div>
      {/* buttonSpecOf: a flat-authored button (label/variant on the block)
          renders on every surface, so it must be editable here too. */}
      <ButtonSpecEditor value={block.button ? { ...buttonSpecOf(block), ...block.button } : buttonSpecOf(block)}
        onChange={(b) => onChange({ ...block, button: b })} />
    </>
  )
}

export function ButtonGroupEditor({ block, onChange }: { block: ButtonGroupBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ButtonGroupBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Gap</label>
        <NumberInput value={block.gap ?? 8} min={0} max={32} onChange={(g) => upd({ gap: g })} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.buttons.map((b, i) => (
          <div key={b.id} style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', right: 4, top: 4, zIndex: 1 }}>
              <ItemControls
                onUp={() => upd({ buttons: moveItem(block.buttons, i, -1), bindings: reindexListBindings(block, 'buttons', moveOrder(block.buttons.length, i, -1)) })}
                onDown={() => upd({ buttons: moveItem(block.buttons, i, 1), bindings: reindexListBindings(block, 'buttons', moveOrder(block.buttons.length, i, 1)) })}
                onRemove={() => upd({ buttons: block.buttons.filter((_, k) => k !== i), bindings: reindexListBindings(block, 'buttons', removeOrder(block.buttons.length, i)) })}
                canUp={i > 0} canDown={i < block.buttons.length - 1}
              />
            </div>
            <ButtonSpecEditor value={b} onChange={(nb) => {
              const next = [...block.buttons]; next[i] = nb; upd({ buttons: next })
            }} label={`Button ${i + 1}`} />
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ buttons: [...block.buttons, makeButton('Button', 'outline')] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add button</button>
      </div>
    </>
  )
}

// ─── Newsletter ──────────────────────────────────────

export function NewsletterEditor({ block, project, transactions = [], onChange }: { block: NewsletterBlock; project: string; transactions?: { obj_name: string; title: string }[]; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<NewsletterBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subheading</label>
        <textarea rows={2} value={block.subheading || ''} onChange={(e) => upd({ subheading: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Email placeholder</label>
        <input value={block.placeholder || ''} onChange={(e) => upd({ placeholder: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Button label</label>
        <input value={block.buttonLabel || ''} onChange={(e) => upd({ buttonLabel: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>Message after subscribing</label>
        <input value={block.success?.message || ''} placeholder="Thanks — you are subscribed."
               onChange={(e) => upd({ success: { ...(block.success || {}), message: e.target.value || undefined } })} style={INPUT} />
      </div>
      <BackgroundEditor value={block.background} project={project} onChange={(bg) => upd({ background: bg })} />
      <FormDestinationEditor project={project} transactions={transactions} block={block}
        fieldNames={['email']} onChange={(patch) => upd(patch)} />
    </>
  )
}

// ─── Payment checkout ────────────────────────────────
//
// Every field here names a COLUMN of the record, never a value. That is the
// block's security model, not a style: the settings reach the browser and
// come back with the checkout request, so a value would be a value a
// customer could edit. The server re-reads the record and resolves the
// amount from `amountColumn` itself.

export function PaymentSettingsEditor({
  block, onChange,
}: {
  block: PaymentSettingsBlock
  onChange: (b: PaymentSettingsBlock) => void
}) {
  const upd = (patch: Partial<PaymentSettingsBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div style={{ ...HINT, marginBottom: 10 }}>
        This block has no bindings and holds no credential. What an
        administrator types goes straight to the server and is encrypted
        there; nothing is ever sent back to the page. Only the project owner
        or a holder of the FUNC-ADMIN role on this application can use it —
        everyone else sees a short notice instead of the form.
      </div>
      <label style={FIELD_LABEL}>Opens on</label>
      <select
        style={INPUT}
        value={block.defaultProvider || 'razorpay'}
        onChange={(e) => upd({ defaultProvider: e.target.value })}
      >
        <option value="razorpay">Razorpay</option>
        <option value="stripe">Stripe</option>
      </select>
      <label style={FIELD_LABEL}>Heading</label>
      <input
        style={INPUT}
        value={block.heading || ''}
        placeholder="Payment settings"
        onChange={(e) => upd({ heading: e.target.value })}
      />
      <label style={FIELD_LABEL}>Description</label>
      <textarea
        style={{ ...INPUT, minHeight: 64 }}
        value={block.description || ''}
        placeholder="Leave blank for the default explanation."
        onChange={(e) => upd({ description: e.target.value })}
      />
    </>
  )
}

export function PaymentCheckoutEditor({
  block, transactions, onChange,
}: {
  block: PaymentCheckoutBlock
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<PaymentCheckoutBlock>) => onChange({ ...block, ...patch })
  const wb = (patch: Partial<NonNullable<PaymentCheckoutBlock['writeback']>>) =>
    upd({ writeback: { ...(block.writeback || {}), ...patch } })
  return (
    <>
      <div><label style={FIELD_LABEL}>Object being paid for</label>
        <select value={block.objName || ''}
                onChange={(e) => upd({ objName: e.target.value })} style={INPUT}>
          <option value="">— choose a transaction —</option>
          {/* A saved object the list doesn't carry would otherwise show as
              unset — keep it visible and selectable. */}
          {block.objName && !transactions.some(t => t.obj_name === block.objName) && (
            <option value={block.objName}>{block.objName} (not in this project&apos;s list)</option>
          )}
          {transactions.map(t => (
            <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>
          ))}
        </select>
      </div>
      <div><label style={FIELD_LABEL}>Page variable holding the record id</label>
        <input value={block.txnIdVar || ''} placeholder="salesOrderId"
               onChange={(e) => upd({ txnIdVar: e.target.value })} style={INPUT} />
        <p style={HINT}>
          Usually written by the order-submit or embed block earlier on this
          page. Nothing can be charged until the record is saved.
        </p>
      </div>
      <div><label style={FIELD_LABEL}>Amount column (required)</label>
        <input value={block.amountColumn || ''} placeholder="TOTAL_AMOUNT"
               onChange={(e) => upd({ amountColumn: e.target.value })} style={INPUT} />
        <p style={HINT}>
          Read from the saved record on the server, in major units
          (3200.50). The browser never sends an amount.
        </p>
      </div>
      <div><label style={FIELD_LABEL}>Who may pay — customer column</label>
        <input value={block.ownerColumn || ''} placeholder="CUSTOMER_ID"
               onChange={(e) => upd({ ownerColumn: e.target.value })} style={INPUT} />
        <p style={HINT}>
          The signed-in site account must match this column. Leave it empty
          and only signed-in users of the application can pay — a public
          storefront needs it set.
        </p>
      </div>
      <div><label style={FIELD_LABEL}>Reference column (receipt)</label>
        <input value={block.referenceColumn || ''} placeholder="ORDER_NO"
               onChange={(e) => upd({ referenceColumn: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Currency column (optional)</label>
        <input value={block.currencyColumn || ''}
               onChange={(e) => upd({ currencyColumn: e.target.value })} style={INPUT} />
        <input value={block.currency || ''} placeholder="Fixed currency, e.g. INR"
               onChange={(e) => upd({ currency: e.target.value.trim().toUpperCase() || undefined })}
               style={{ ...INPUT, marginTop: 6 }} />
        <p style={HINT}>
          A fixed ISO code wins when set; otherwise the column&apos;s value;
          both empty ⇒ the gateway&apos;s configured currency.
        </p>
      </div>
      <div><label style={FIELD_LABEL}>Payer name / email / phone columns</label>
        <input value={block.payerNameColumn || ''} placeholder="Name column"
               onChange={(e) => upd({ payerNameColumn: e.target.value })} style={INPUT} />
        <input value={block.payerEmailColumn || ''} placeholder="Email column"
               onChange={(e) => upd({ payerEmailColumn: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
        <input value={block.payerContactColumn || ''} placeholder="Phone column"
               onChange={(e) => upd({ payerContactColumn: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
        <p style={HINT}>Prefills the gateway&apos;s own form. Optional.</p>
      </div>

      <div><label style={FIELD_LABEL}>Mark the record paid — status column</label>
        <input value={block.writeback?.statusColumn || ''} placeholder="PAY_STATUS"
               onChange={(e) => wb({ statusColumn: e.target.value })} style={INPUT} />
        <input value={block.writeback?.statusValue || ''} placeholder="PAID"
               onChange={(e) => wb({ statusValue: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
        <p style={HINT}>
          Stamped once the money is captured. A record already reading this
          value cannot be charged again.
        </p>
      </div>
      <div><label style={FIELD_LABEL}>Payment id / paid-at columns</label>
        <input value={block.writeback?.paymentIdColumn || ''} placeholder="PAYMENT_REF"
               onChange={(e) => wb({ paymentIdColumn: e.target.value })} style={INPUT} />
        <input value={block.writeback?.paidAtColumn || ''} placeholder="PAID_ON"
               onChange={(e) => wb({ paidAtColumn: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
      </div>
      <div><label style={FIELD_LABEL}>Paid amount / method columns</label>
        <input value={block.writeback?.paidAmountColumn || ''} placeholder="PAID_AMOUNT"
               onChange={(e) => wb({ paidAmountColumn: e.target.value })} style={INPUT} />
        <input value={block.writeback?.methodColumn || ''} placeholder="PAY_METHOD"
               onChange={(e) => wb({ methodColumn: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
      </div>

      <div><label style={FIELD_LABEL}>Button label</label>
        <input value={block.buttonLabel || ''} placeholder="Pay now"
               onChange={(e) => upd({ buttonLabel: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''}
               onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Description</label>
        <textarea value={block.description || ''} rows={2}
                  onChange={(e) => upd({ description: e.target.value })}
                  style={{ ...INPUT, resize: 'vertical' }} />
      </div>
      <div><label style={FIELD_LABEL}>After a successful payment</label>
        <input value={block.successTitle || ''} placeholder="Payment received"
               onChange={(e) => upd({ successTitle: e.target.value })} style={INPUT} />
        <textarea value={block.successBody || ''} rows={2} placeholder="Message under the title (optional)"
                  onChange={(e) => upd({ successBody: e.target.value })}
                  style={{ ...INPUT, marginTop: 6, resize: 'vertical' }} />
        <input value={block.onSuccessNavigateTo || ''} placeholder="Go to page (optional URL)"
               onChange={(e) => upd({ onSuccessNavigateTo: e.target.value })}
               style={{ ...INPUT, marginTop: 6 }} />
      </div>
      <div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!block.showCodOption}
                 onChange={(e) => upd({ showCodOption: e.target.checked })} />
          Also offer cash on delivery
        </label>
        {block.showCodOption && (
          <input value={block.codLabel || ''} placeholder="Cash on delivery"
                 onChange={(e) => upd({ codLabel: e.target.value })}
                 style={{ ...INPUT, marginTop: 6 }} />
        )}
        <p style={HINT}>
          Shown only when the project&apos;s Payments setup allows it. It takes
          no money — it records the choice on the order.
        </p>
      </div>
      <div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={!!block.hideUntilRecord}
                 onChange={(e) => upd({ hideUntilRecord: e.target.checked || undefined })} />
          Hide until the record exists
        </label>
        <p style={HINT}>
          The payment step appears once the page variable above holds a record
          (e.g. after the checkout places the order).
        </p>
      </div>
    </>
  )
}

// ─── Contact form ────────────────────────────────────

export function ContactFormEditor({ block, project = '', transactions = [], onChange }: { block: ContactFormBlock; project?: string; transactions?: { obj_name: string; title: string }[]; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ContactFormBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <FormDestinationEditor project={project} transactions={transactions} block={block}
        fieldNames={block.fields.map((f) => f.name).filter(Boolean)} onChange={(patch) => upd(patch)} />
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Subheading</label>
        <textarea rows={2} value={block.subheading || ''} onChange={(e) => upd({ subheading: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div><label style={FIELD_LABEL}>Button label</label>
        <input value={block.buttonLabel} onChange={(e) => upd({ buttonLabel: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(v) => upd({ align: v })} />
      </div>
      <div><label style={FIELD_LABEL}>After sending</label>
        <input value={block.success?.title || ''} placeholder="Thank you"
               onChange={(e) => upd({ success: { ...(block.success || {}), title: e.target.value || undefined } })} style={INPUT} />
        <input value={block.success?.message || ''} placeholder="We have your message."
               onChange={(e) => upd({ success: { ...(block.success || {}), message: e.target.value || undefined } })}
               style={{ ...INPUT, marginTop: 6 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {block.fields.map((f, i) => (
          <div key={f.id} style={SUBSECTION}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Field {i + 1}</label>
              <ItemControls
                onUp={() => upd({ fields: moveItem(block.fields, i, -1) })}
                onDown={() => upd({ fields: moveItem(block.fields, i, 1) })}
                onRemove={() => upd({ fields: block.fields.filter((_, k) => k !== i) })}
                canUp={i > 0} canDown={i < block.fields.length - 1}
              />
            </div>
            <div style={TWO_COL}>
              <input value={f.name} onChange={(e) => {
                const fields = [...block.fields]; fields[i] = { ...f, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }; upd({ fields })
              }} placeholder="name" style={{ ...INPUT, fontFamily: 'monospace' }} />
              <select value={f.type} onChange={(e) => {
                const fields = [...block.fields]; fields[i] = { ...f, type: e.target.value as ContactFormField['type'] }; upd({ fields })
              }} style={INPUT}>
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="textarea">Textarea</option>
                {!['text', 'email', 'tel', 'textarea'].includes(f.type) && <option value={f.type}>{f.type} (unrecognised)</option>}
              </select>
            </div>
            <input value={f.label} onChange={(e) => {
              const fields = [...block.fields]; fields[i] = { ...f, label: e.target.value }; upd({ fields })
            }} placeholder="Label" style={INPUT} />
            <input value={f.placeholder || ''} onChange={(e) => {
              const fields = [...block.fields]; fields[i] = { ...f, placeholder: e.target.value }; upd({ fields })
            }} placeholder="Placeholder" style={INPUT} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.required} onChange={(e) => {
                const fields = [...block.fields]; fields[i] = { ...f, required: e.target.checked }; upd({ fields })
              }} />
              Required
            </label>
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); upd({ fields: [...block.fields, { id: newId('fld'), name: `field_${block.fields.length + 1}`, label: 'Field', type: 'text' }] }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add field</button>
      </div>
    </>
  )
}

// ─── Callout ─────────────────────────────────────────

export function CalloutEditor({ block, onChange }: { block: CalloutBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CalloutBlock>) => onChange({ ...block, ...patch })
  const iconPosition = block.iconPosition || 'left'
  const iconSize = block.iconSize || 'md'
  return (
    <>
      <div><label style={FIELD_LABEL}>Variant</label>
        <select value={block.variant} onChange={(e) => upd({ variant: e.target.value as CalloutVariant })} style={INPUT}>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
          <option value="tip">Tip</option>
        </select>
      </div>
      <div><label style={FIELD_LABEL}>Icon (emoji or material-icon name, optional)</label>
        <input value={block.icon || ''} onChange={(e) => upd({ icon: e.target.value })} style={INPUT} placeholder="e.g. 💡 or 'lightbulb'" />
      </div>
      <div><label style={FIELD_LABEL}>Title</label>
        <input value={block.title || ''} onChange={(e) => upd({ title: e.target.value })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Body</label>
        <textarea rows={4} value={block.body} onChange={(e) => upd({ body: e.target.value })} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>

      {/* ── Placement & spacing ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL}>Icon position</label>
          <select
            value={iconPosition}
            onChange={(e) => upd({ iconPosition: e.target.value as CalloutIconPosition })}
            style={INPUT}
          >
            <option value="left">Left</option>
            <option value="top">Top</option>
            <option value="none">No icon</option>
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL}>Icon size</label>
          <select
            value={iconSize}
            onChange={(e) => upd({ iconSize: e.target.value as CalloutIconSize })}
            style={INPUT}
            disabled={iconPosition === 'none'}
          >
            <option value="sm">Small (16px)</option>
            <option value="md">Medium (20px)</option>
            <option value="lg">Large (28px)</option>
          </select>
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL}>Text alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(v) => upd({ align: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL}>Padding (px)</label>
          <NumberInput value={block.padding ?? 14} min={0} max={64} onChange={(n) => upd({ padding: n })} />
        </div>
        <div>
          <label style={FIELD_LABEL}>Icon ↔ text gap (px)</label>
          <NumberInput value={block.gap ?? 12} min={0} max={48} onChange={(n) => upd({ gap: n })} />
        </div>
      </div>
    </>
  )
}

// ─── eCommerce editors ──────────────────────────────
//
// All ecommerce blocks now use the same ArgMapping-based binding
// surface as transaction embeds. The data source is configurable
// (built-in API endpoint or — for picker / product-selection — a
// transaction object). See livepreview/bindingResolver.ts for the
// input vs output convention.

// ─── Single ArgMapping editor ───────────────────────
// Wraps ArgMappingEditor for the common single-row case (cartBinding,
// gateBinding, etc.) where the block has exactly one input/output and
// rendering a "Add row" / "Remove" affordance would be noise.
function SingleBindingEditor({
  label, hint, page, project, value, target = 'value', availableTargets, onChange,
}: {
  label: string
  hint?: string
  page: PageModel
  project?: string
  value: ArgMapping | undefined
  target?: string
  availableTargets?: ArgTargetOption[]
  onChange: (next: ArgMapping | undefined) => void
}) {
  const cur = value || { ...makeArgMapping(target), source: { source: 'page_input', name: '' } }
  return (
    <div>
      <label style={FIELD_LABEL}>{label}</label>
      <ArgMappingEditor
        args={[cur]}
        inputs={page.inputs || []}
        project={project}
        availableTargets={availableTargets || [{ name: target, label }]}
        title=""
        onChange={(next) => {
          // Keep a single row only; if user removed it, emit undefined.
          if (next.length === 0) onChange(undefined)
          else onChange(next[0])
        }}
      />
      {hint && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>{hint}</p>}
    </div>
  )
}

// ─── EcomSourceEditor ───────────────────────────────
// Shared source-config UI for ecommerce blocks. Two source modes:
//   - api: built-in endpoint dropdown + optional custom URL + arg
//     mapping editor (params bound to page vars).
//   - transaction: ObjectPickerField + filters editor (rendered like
//     EmbedEditor's T branch but trimmed — we don't pull the full
//     model here because picker blocks just render rows, they don't
//     need column metadata to map filters to targets).
function EcomSourceEditor({
  block, source, allowTransaction, allowedEndpointIds, transactions, page, project, onChange,
}: {
  block: { id: string }
  source: EcomSource
  allowTransaction: boolean
  /** When set, only these built-in endpoints appear in the dropdown.
   *  Useful for OrderSubmit which should only POST to sales_orders or
   *  a custom URL — not GET customers. */
  allowedEndpointIds?: EcomEndpointId[]
  transactions: { obj_name: string; title: string }[]
  page: PageModel
  project?: string
  onChange: (next: EcomSource) => void
}) {
  const kind = source.kind
  const apiSource = kind === 'api' ? source : null
  const txnSource = kind === 'transaction' ? source : null

  const endpointDef = apiSource ? getEndpointDef(apiSource.endpointId) : null
  const endpointArgTargets: ArgTargetOption[] = endpointDef
    ? endpointDef.argTargets.map((a) => ({ name: a.name, label: a.label, hint: a.hint }))
    : []

  const allowedOptions = allowedEndpointIds
    ? ECOM_ENDPOINT_OPTIONS.filter((o) => allowedEndpointIds.includes(o.id))
    : ECOM_ENDPOINT_OPTIONS
  // A saved endpoint outside the allowed list still has to show — otherwise
  // the select displays the first option over a value it doesn't hold.
  const endpointOptions = apiSource && !allowedOptions.some((o) => o.id === apiSource.endpointId)
    ? [
        ECOM_ENDPOINT_OPTIONS.find((o) => o.id === apiSource.endpointId)
          ?? { id: apiSource.endpointId, label: String(apiSource.endpointId) },
        ...allowedOptions,
      ]
    : allowedOptions

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <label style={FIELD_LABEL}>Source</label>
        <select
          value={kind}
          onChange={(e) => {
            const nextKind = e.target.value as 'api' | 'transaction'
            if (nextKind === 'api') {
              onChange({ kind: 'api', endpointId: allowedEndpointIds?.[0] ?? 'customers' } as EcomSourceApi)
            } else {
              onChange({ kind: 'transaction', obj_name: '', filters: [], args: [] } as EcomSourceTransaction)
            }
          }}
          style={INPUT}
        >
          <option value="api">REST API endpoint</option>
          {allowTransaction && <option value="transaction">Transaction object</option>}
        </select>
      </div>

      {apiSource && (
        <>
          <div>
            <label style={FIELD_LABEL}>Endpoint</label>
            <select
              value={apiSource.endpointId}
              onChange={(e) => onChange({ ...apiSource, endpointId: e.target.value as EcomEndpointId })}
              style={INPUT}
            >
              {endpointOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            {endpointDef && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                <code style={{ fontFamily: 'monospace' }}>{endpointDef.method} {endpointDef.url}</code>
                {endpointDef.description ? <> · {endpointDef.description}</> : null}
              </p>
            )}
          </div>
          {apiSource.endpointId === 'custom' && (
            <div>
              <label style={FIELD_LABEL}>Custom URL</label>
              <input
                value={apiSource.customUrl || ''}
                onChange={(e) => onChange({ ...apiSource, customUrl: e.target.value })}
                style={{ ...INPUT, fontFamily: 'monospace' }}
                placeholder="/api/custom/path or https://…"
              />
            </div>
          )}
          <ArgMappingEditor
            args={apiSource.args}
            inputs={page.inputs || []}
            project={project}
            availableTargets={endpointArgTargets}
            metaUnavailableHint={apiSource.endpointId === 'custom' ? 'Custom URL — type any parameter name.' : undefined}
            title="Request parameters"
            emptyHint="No parameters bound. Add a row to pass a page input or static value as a query/body field."
            onChange={(args) => onChange({ ...apiSource, args })}
          />
        </>
      )}

      {txnSource && (
        <>
          <ObjectPickerField
            label="Target transaction"
            value={txnSource.obj_name}
            onChange={(name) => onChange({ ...txnSource, obj_name: name, filters: [] })}
            catalog={transactions.map((t) => ({ obj_name: t.obj_name, title: t.title }))}
            typeLabel="transaction"
            emptyHint="No transactions in this project — pick one from the Workbench navigation, or use a custom name."
          />
          <TxnFiltersEditor
            filters={txnSource.filters || []}
            page={page}
            project={project}
            onChange={(filters) => onChange({ ...txnSource, filters })}
          />
          <ArgMappingEditor
            args={txnSource.args}
            inputs={page.inputs || []}
            project={project}
            availableTargets={[]}
            metaUnavailableHint="Transaction column metadata isn't loaded here — type any column name."
            title="Pre-fill args (optional)"
            emptyHint="No pre-fills. Add a row to inject a value into a transaction column when a row is selected."
            onChange={(args) => onChange({ ...txnSource, args })}
          />
        </>
      )}
    </div>
  )
}

// Minimal filter editor for transaction-mode ecommerce sources. Loads
// no model — column name is free-text and op is a small fixed list.
// (The transaction embed has a richer version that also loads columns;
// kept separate here so ecommerce blocks don't need to fetch the model
// just to render their property panel.)
// One filter operand: a page input / project variable, or a static value.
// A saved variable name the page no longer declares stays listed (flagged)
// rather than blanking the select — blanking it hid the binding and the next
// edit would have thrown it away.
function TxnValueRefRow({
  label, value, inputs, projectVars, onChange,
}: {
  label?: string
  value: FilterValueRef | undefined
  inputs: PageInput[]
  projectVars: { value: string; label: string }[]
  onChange: (v: FilterValueRef) => void
}) {
  const source = value?.source || 'page_input'
  const name = value?.name || ''
  const undeclared = !!name && !inputs.some((i) => i.name === name) && !projectVars.some((pv) => pv.value === name)
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {label && <span style={{ fontSize: '0.8125rem', width: 34, color: 'var(--color-text-secondary)' }}>{label}</span>}
      <select
        value={source}
        onChange={(e) => {
          const s = e.target.value as 'page_input' | 'static'
          onChange(s === 'page_input' ? { source: 'page_input', name: '' } : { source: 'static', value: '' })
        }}
        style={{ ...INPUT, width: 110 }}
      >
        <option value="page_input">Page input</option>
        <option value="static">Static</option>
      </select>
      {source === 'page_input' ? (
        <select
          value={name}
          onChange={(e) => onChange({ source: 'page_input', name: e.target.value })}
          style={{ ...INPUT, flex: 1 }}
        >
          <option value="">— Pick input —</option>
          {undeclared && <option value={name}>{name} (not declared on this page)</option>}
          {inputs.length > 0 && (
            <optgroup label="Page inputs">
              {inputs.map((inp) => (
                <option key={inp.name} value={inp.name}>{inp.label || inp.name}</option>
              ))}
            </optgroup>
          )}
          {projectVars.length > 0 && (
            <optgroup label="Project variables">
              {projectVars.map((pv) => (
                <option key={pv.value} value={pv.value}>{pv.label}</option>
              ))}
            </optgroup>
          )}
        </select>
      ) : (
        <input
          value={value?.value || ''}
          onChange={(e) => onChange({ source: 'static', value: e.target.value })}
          placeholder="Static value"
          style={{ ...INPUT, flex: 1 }}
        />
      )}
    </div>
  )
}

function TxnFiltersEditor({
  filters, page, project, onChange,
}: {
  filters: EmbedFilter[]
  page: PageModel
  project?: string
  onChange: (next: EmbedFilter[]) => void
}) {
  const inputs = page.inputs || []
  const projectVars = useProjectVariables(project)
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label style={FIELD_LABEL}>Filters</label>
        <button onClick={(e) => { e.stopPropagation(); onChange([...filters, makeFilter()]) }} className="btn btn-ghost btn-sm">
          <Plus size={13} /> Add
        </button>
      </div>
      {filters.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          No filters. Add one to bind a page variable to a transaction column.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filters.map((f) => (
          <div key={f.id} style={{
            padding: 8, border: '1px solid var(--color-border)', borderRadius: 6,
            background: 'var(--color-bg)', display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: 6 }}>
              <input
                value={f.column}
                onChange={(e) => onChange(filters.map((x) => x.id === f.id ? { ...x, column: e.target.value } : x))}
                placeholder="column"
                style={{ ...INPUT, fontFamily: 'monospace' }}
              />
              <select
                value={f.op}
                onChange={(e) => onChange(filters.map((x) => x.id === f.id ? { ...x, op: e.target.value as EmbedFilter['op'] } : x))}
                style={INPUT}
              >
                <option value="eq">=</option>
                <option value="neq">≠</option>
                <option value="gt">&gt;</option>
                <option value="gte">≥</option>
                <option value="lt">&lt;</option>
                <option value="lte">≤</option>
                <option value="between">between</option>
                <option value="contains">contains</option>
                <option value="starts_with">starts with</option>
              </select>
              <button
                onClick={(e) => { e.stopPropagation(); onChange(filters.filter((x) => x.id !== f.id)) }}
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--color-error)' }}
                title="Remove"
              >
                <Trash2 size={13} />
              </button>
            </div>
            {f.op === 'between' ? (
              <>
                <TxnValueRefRow label="From" value={f.from} inputs={inputs} projectVars={projectVars}
                  onChange={(v) => onChange(filters.map((x) => x.id === f.id ? { ...x, from: v } : x))} />
                <TxnValueRefRow label="To" value={f.to} inputs={inputs} projectVars={projectVars}
                  onChange={(v) => onChange(filters.map((x) => x.id === f.id ? { ...x, to: v } : x))} />
              </>
            ) : (
              <TxnValueRefRow value={f.value} inputs={inputs} projectVars={projectVars}
                onChange={(v) => onChange(filters.map((x) => x.id === f.id ? { ...x, value: v } : x))} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Output target options for a given endpoint id. Combines built-in
// definitions with a fallback "value" target for transaction sources
// (the whole picked row) or unknown endpoints.
function outputTargetsFor(source: EcomSource): ArgTargetOption[] {
  if (source.kind === 'transaction') {
    return [
      { name: 'value', label: 'Selected row (whole)' },
      { name: 'id', label: 'Primary key (_txn_id)' },
    ]
  }
  const def = getEndpointDef(source.endpointId)
  if (!def) return [{ name: 'value', label: 'Selected record (whole)' }]
  return def.outputTargets.map((o) => ({ name: o.name, label: o.label, hint: o.hint }))
}

// ─── EntityPickerEditor (was CustomerPickerEditor) ─
export function EntityPickerEditor({
  block, page, project, transactions, onChange,
}: {
  block: EntityPickerBlock
  page: PageModel
  project: string
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<EntityPickerBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <EcomSourceEditor
        block={block}
        source={block.source}
        allowTransaction
        transactions={transactions}
        page={page}
        project={project}
        onChange={(source) => upd({ source })}
      />
      <ArgMappingEditor
        args={block.outputBindings}
        inputs={page.inputs || []}
        project={project}
        availableTargets={outputTargetsFor(block.source)}
        title="Output bindings (write picked record to vars)"
        emptyHint="No outputs configured. Add a row to write the picked record (or one of its fields) to a page variable."
        onChange={(outputBindings) => upd({ outputBindings })}
      />
      <SingleBindingEditor
        label='"+ New" toggle binding (optional)'
        hint='Boolean page var flipped TRUE when the "+ New" button is clicked. NewCustomerForm reads this same var for visibility.'
        page={page}
        project={project}
        value={block.toggleBinding}
        onChange={(toggleBinding) => upd({ toggleBinding })}
      />
      <div>
        <label style={FIELD_LABEL}>Search placeholder</label>
        <input value={block.searchPlaceholder || ''} onChange={(e) => upd({ searchPlaceholder: e.target.value })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Layout</label>
        <select value={block.layout || 'spacious'} onChange={(e) => upd({ layout: e.target.value as 'compact' | 'spacious' })} style={INPUT}>
          <option value="spacious">Spacious</option>
          <option value="compact">Compact</option>
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" id={`ep-create-${block.id}`} checked={block.showCreateNewToggle !== false}
          onChange={(e) => upd({ showCreateNewToggle: e.target.checked })} />
        <label htmlFor={`ep-create-${block.id}`} style={{ fontSize: '0.9125rem' }}>Show "+ New" affordance</label>
      </div>
    </>
  )
}

// ─── NewCustomerFormEditor ──────────────────────────
export function NewCustomerFormEditor({
  block, page, project, onChange,
}: {
  block: NewCustomerFormBlock
  page: PageModel
  project: string
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<NewCustomerFormBlock>) => onChange({ ...block, ...patch })
  const isCustom = block.source.endpointId === 'custom'
  return (
    <>
      <EcomSourceEditor
        block={block}
        source={block.source}
        allowTransaction={false}
        // Form POSTs only — limit to write endpoints + custom.
        allowedEndpointIds={['customers', 'sales_orders', 'custom']}
        transactions={[]}
        page={page}
        project={project}
        onChange={(source) => {
          if (source.kind !== 'api') return
          upd({ source })
        }}
      />
      {isCustom && (
        <div style={{
          padding: 8, border: '1px dashed var(--color-border)', borderRadius: 6,
          fontSize: 13, color: 'var(--color-text-secondary)',
        }}>
          Custom URL — the runtime falls back to a raw JSON body editor (no
          schema-driven fields). Use the default "Customers" endpoint to get
          schema-driven form fields.
        </div>
      )}
      <ArgMappingEditor
        args={block.outputBindings}
        inputs={page.inputs || []}
        project={project}
        availableTargets={outputTargetsFor(block.source)}
        title="Output bindings (write created record to vars)"
        onChange={(outputBindings) => upd({ outputBindings })}
      />
      <SingleBindingEditor
        label="Visibility toggle binding (optional)"
        hint="Boolean page var — when TRUE the form is shown. On submit/cancel it auto-flips back to FALSE."
        page={page}
        project={project}
        value={block.toggleBinding}
        onChange={(toggleBinding) => upd({ toggleBinding })}
      />
      <div>
        <label style={FIELD_LABEL}>Layout</label>
        <select value={block.layout || 'single-column'} onChange={(e) => upd({ layout: e.target.value as NewCustomerFormBlock['layout'] })} style={INPUT}>
          <option value="single-column">Single column</option>
          <option value="two-column">Two columns</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Submit label</label>
        <input value={block.submitLabel || ''} onChange={(e) => upd({ submitLabel: e.target.value })} style={INPUT} placeholder="Save customer" />
      </div>
    </>
  )
}

// ─── ProductSelectionEditor ─────────────────────────
export function ProductSelectionEditor({
  block, page, project, transactions, onChange,
}: {
  block: ProductSelectionBlock
  page: PageModel
  project: string
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<ProductSelectionBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <EcomSourceEditor
        block={block}
        source={block.source}
        allowTransaction
        transactions={transactions}
        page={page}
        project={project}
        onChange={(source) => upd({ source })}
      />
      <SingleBindingEditor
        label="Cart binding (where line items accumulate)"
        hint="Page variable (list) where configured items accumulate. The block reads from AND writes to this var."
        page={page}
        project={project}
        value={block.cartBinding}
        target="value"
        availableTargets={[{ name: 'value', label: 'Cart array' }]}
        onChange={(cartBinding) => {
          if (!cartBinding) return
          upd({ cartBinding })
        }}
      />
      <SingleBindingEditor
        label="Gate binding (optional)"
        hint="Optional read binding — when set and the bound var is falsy, the picker shows a gate (e.g. 'Pick a customer first'). Leave empty to never gate."
        page={page}
        project={project}
        value={block.gateBinding}
        target="value"
        availableTargets={[{ name: 'value', label: 'Gate value' }]}
        onChange={(gateBinding) => upd({ gateBinding })}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL}>Inline layout</label>
          <select value={block.layout || 'grid'} onChange={(e) => upd({ layout: e.target.value as 'grid' | 'list' })} style={INPUT}>
            <option value="grid">Grid</option>
            <option value="list">List</option>
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL}>Inline columns</label>
          <select value={String(block.columns ?? 3)} onChange={(e) => upd({ columns: Number(e.target.value) as 2 | 3 | 4 })} style={INPUT}>
            <option value="2">2</option><option value="3">3</option><option value="4">4</option>
          </select>
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL}>Selector mode</label>
        <select
          value={
            block.selectorMode ||
            (block.catalogueMode === 'modal-only' ? 'catalogue'
              : block.catalogueMode === 'both' ? 'both'
              : 'wizard')
          }
          onChange={(e) => upd({ selectorMode: e.target.value as ProductSelectionBlock['selectorMode'] })}
          style={INPUT}
        >
          <option value="wizard">Wizard — product → style → SKU on the doc</option>
          <option value="catalogue">Catalogue — full-screen browse only</option>
          <option value="both">Both — wizard inline with catalogue affordance</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Embedded preview</label>
        <select
          value={block.previewDomain || 'none'}
          onChange={(e) => upd({ previewDomain: e.target.value as ProductSelectionBlock['previewDomain'] })}
          style={INPUT}
        >
          <option value="none">None</option>
          <option value="garment">Garment — Sketch / Photo / AI tabs</option>
          <option value="gallery">Gallery — hero + thumbnails (e-commerce style)</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Wizard opens on</label>
        <select
          value={block.initialStep || 'product'}
          onChange={(e) => upd({ initialStep: e.target.value as ProductSelectionBlock['initialStep'] })}
          style={INPUT}
        >
          <option value="product">Product</option>
          <option value="style">Style</option>
          <option value="customize">Customize</option>
        </select>
      </div>
      {(block.selectorMode === 'both' || block.selectorMode === 'catalogue' ||
        (!block.selectorMode && block.catalogueMode !== 'inline-only')) && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={FIELD_LABEL}>Browse button label</label>
              <input value={block.catalogueButtonLabel || ''} onChange={(e) => upd({ catalogueButtonLabel: e.target.value })} style={INPUT} placeholder="Browse catalogue" />
            </div>
            <div>
              <label style={FIELD_LABEL}>Catalogue title</label>
              <input value={block.catalogueTitle || ''} onChange={(e) => upd({ catalogueTitle: e.target.value })} style={INPUT} placeholder="Catalogue" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={FIELD_LABEL}>Catalogue columns</label>
              <select value={String(block.catalogueColumns ?? 4)} onChange={(e) => upd({ catalogueColumns: Number(e.target.value) as 3 | 4 | 5 })} style={INPUT}>
                <option value="3">3</option><option value="4">4</option><option value="5">5</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
                <input type="checkbox" checked={block.showCategorySidebar !== false}
                  onChange={(e) => upd({ showCategorySidebar: e.target.checked })} />
                Sidebar
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
                <input type="checkbox" checked={block.showCartDrawer !== false}
                  onChange={(e) => upd({ showCartDrawer: e.target.checked })} />
                Cart drawer
              </label>
            </div>
          </div>
          <SingleBindingEditor
            label="Open-modal binding (optional)"
            hint="Boolean page var — set to TRUE to programmatically open the catalogue modal, FALSE to close."
            page={page}
            project={project}
            value={block.openInModalBinding}
            onChange={(openInModalBinding) => upd({ openInModalBinding })}
          />
        </>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" id={`ps-price-${block.id}`} checked={!!block.allowPriceOverride}
          onChange={(e) => upd({ allowPriceOverride: e.target.checked })} />
        <label htmlFor={`ps-price-${block.id}`} style={{ fontSize: '0.9125rem' }}>Allow per-line price override</label>
      </div>
    </>
  )
}

// ─── ProductPreviewEditor ───────────────────────────
export function ProductPreviewEditor({ block, page, project, onChange }: { block: ProductPreviewBlock; page: PageModel; project?: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ProductPreviewBlock>) => onChange({ ...block, ...patch })
  const source = block.attributesSource || 'product_selection_state'
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Attribute source</label>
        <select value={source} onChange={(e) => upd({ attributesSource: e.target.value as ProductPreviewBlock['attributesSource'] })} style={INPUT}>
          <option value="product_selection_state">Live from product_selection (recommended)</option>
          <option value="pageVar">Page variables</option>
        </select>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {source === 'product_selection_state'
            ? 'Reads the picked product / style / attributes directly from the product_selection block on this page.'
            : 'Reads from explicit page-variable bindings — useful for stand-alone preview surfaces outside an order-capture flow.'}
        </p>
      </div>
      {source === 'pageVar' && (
        <>
          <SingleBindingEditor
            label="Product binding"
            page={page}
            project={project}
            value={block.productBinding}
            onChange={(productBinding) => upd({ productBinding })}
          />
          <SingleBindingEditor
            label="Style binding"
            page={page}
            project={project}
            value={block.styleBinding}
            onChange={(styleBinding) => upd({ styleBinding })}
          />
          <SingleBindingEditor
            label="Attributes binding"
            hint="Object map of preference key → selected value (e.g. { color: 'navy', collar: 'spread' })."
            page={page}
            project={project}
            value={block.attributesBinding}
            onChange={(attributesBinding) => upd({ attributesBinding })}
          />
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL}>Layout</label>
          <select value={block.layout || 'card'} onChange={(e) => upd({ layout: e.target.value as ProductPreviewBlock['layout'] })} style={INPUT}>
            <option value="card">Card</option>
            <option value="large">Large</option>
            <option value="inline">Inline</option>
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL}>Aspect ratio</label>
          <select value={block.aspect || '1:1'} onChange={(e) => upd({ aspect: e.target.value as ProductPreviewBlock['aspect'] })} style={INPUT}>
            <option value="1:1">1:1 (square)</option>
            <option value="4:3">4:3</option>
            <option value="3:4">3:4 (portrait)</option>
            <option value="16:9">16:9</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={block.showCaption !== false} onChange={(e) => upd({ showCaption: e.target.checked })} />
          Show caption
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={block.showAttributeChips !== false} onChange={(e) => upd({ showAttributeChips: e.target.checked })} />
          Show attribute chips
        </label>
      </div>
      <div>
        <label style={FIELD_LABEL}>Empty-state title</label>
        <input value={block.emptyStateTitle || ''} onChange={(e) => upd({ emptyStateTitle: e.target.value })} style={INPUT} placeholder="Pick a product to see a preview" />
      </div>
      <div>
        <label style={FIELD_LABEL}>Empty-state body</label>
        <input value={block.emptyStateBody || ''} onChange={(e) => upd({ emptyStateBody: e.target.value })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Placement master id (design-time)</label>
        <input
          value={block.placementMasterId || ''}
          onChange={(e) => upd({ placementMasterId: e.target.value.replace(/[^A-Za-z0-9_\-]/g, '') || undefined })}
          style={INPUT}
          placeholder="e.g. prod_001 or shirt_master"
        />
      </div>
    </>
  )
}

// ─── OrderSummaryEditor ─────────────────────────────
export function OrderSummaryEditor({ block, page, project, onChange }: { block: OrderSummaryBlock; page: PageModel; project?: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<OrderSummaryBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <SingleBindingEditor
        label="Cart binding (reads + writes the cart array)"
        page={page}
        project={project}
        value={block.cartBinding}
        target="value"
        availableTargets={[{ name: 'value', label: 'Cart array' }]}
        onChange={(cartBinding) => {
          if (!cartBinding) return
          upd({ cartBinding })
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={block.editable !== false}
            onChange={(e) => upd({ editable: e.target.checked })} />
          Allow row edit / remove
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={block.showSubtotal !== false}
            onChange={(e) => upd({ showSubtotal: e.target.checked })} />
          Show subtotal
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={!!block.showTax}
            onChange={(e) => upd({ showTax: e.target.checked })} />
          Show tax
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem' }}>
          <input type="checkbox" checked={!!block.showDiscount}
            onChange={(e) => upd({ showDiscount: e.target.checked })} />
          Show discount
        </label>
      </div>
      <div>
        <label style={FIELD_LABEL}>Empty-state title</label>
        <input value={block.emptyStateTitle || ''} onChange={(e) => upd({ emptyStateTitle: e.target.value })} style={INPUT} placeholder="No items yet" />
      </div>
      <div>
        <label style={FIELD_LABEL}>Empty-state body</label>
        <input value={block.emptyStateBody || ''} onChange={(e) => upd({ emptyStateBody: e.target.value })} style={INPUT} placeholder="Pick a product to begin." />
      </div>
    </>
  )
}

// ─── OrderSubmitEditor ──────────────────────────────
export function OrderSubmitEditor({
  block, page, project, onChange,
}: {
  block: OrderSubmitBlock
  page: PageModel
  project: string
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<OrderSubmitBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <EcomSourceEditor
        block={block}
        source={block.source}
        allowTransaction={false}
        allowedEndpointIds={['sales_orders', 'custom']}
        transactions={[]}
        page={page}
        project={project}
        onChange={(source) => {
          if (source.kind !== 'api') return
          upd({ source })
        }}
      />
      <SingleBindingEditor
        label="Customer binding (read)"
        page={page}
        project={project}
        value={block.customerBinding}
        onChange={(customerBinding) => {
          if (!customerBinding) return
          upd({ customerBinding })
        }}
      />
      <SingleBindingEditor
        label="Cart binding (read)"
        page={page}
        project={project}
        value={block.cartBinding}
        onChange={(cartBinding) => {
          if (!cartBinding) return
          upd({ cartBinding })
        }}
      />
      <ArgMappingEditor
        args={block.outputBindings}
        inputs={page.inputs || []}
        project={project}
        availableTargets={outputTargetsFor(block.source)}
        title="Output bindings (write order response to vars)"
        onChange={(outputBindings) => upd({ outputBindings })}
      />
      <div>
        <label style={FIELD_LABEL}>Button label</label>
        <input value={block.buttonLabel || ''} onChange={(e) => upd({ buttonLabel: e.target.value })} style={INPUT} placeholder="Place Order" />
      </div>
      <div>
        <label style={FIELD_LABEL}>Success title</label>
        <input value={block.successTitle || ''} onChange={(e) => upd({ successTitle: e.target.value })} style={INPUT} placeholder="Order {{orderNumber}} created" />
      </div>
      <div>
        <label style={FIELD_LABEL}>Success body</label>
        <input value={block.successBody || ''} onChange={(e) => upd({ successBody: e.target.value })} style={INPUT} placeholder="The order has been placed in Vision ERP." />
      </div>
      <div>
        <label style={FIELD_LABEL}>Navigate to (optional URL)</label>
        <input value={block.onSuccessNavigateTo || ''} onChange={(e) => upd({ onSuccessNavigateTo: e.target.value })} style={INPUT} placeholder="https://..." />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" id={`os-startnew-${block.id}`} checked={block.showStartNew !== false}
          onChange={(e) => upd({ showStartNew: e.target.checked })} />
        <label htmlFor={`os-startnew-${block.id}`} style={{ fontSize: '0.9125rem' }}>Show "Start new order" after success</label>
      </div>
    </>
  )
}

// ─── FileUploadEditor ───────────────────────────────
// Reusable "fixed value OR page variable" control for a FilterValueRef.
function FilterValueRefControl({
  label, value, inputs, onChange,
}: {
  label: string
  value: FilterValueRef | undefined
  inputs: PageInput[]
  onChange: (v: FilterValueRef) => void
}) {
  const source = value?.source || 'static'
  return (
    <div>
      <label style={FIELD_LABEL}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={source}
          onChange={(e) => {
            const s = e.target.value as 'page_input' | 'static'
            onChange(s === 'page_input'
              ? { source: 'page_input', name: '' }
              : { source: 'static', value: '' })
          }}
          style={{ ...INPUT, width: 120 }}
        >
          <option value="static">Fixed value</option>
          <option value="page_input">Page variable</option>
        </select>
        {source === 'page_input' ? (
          <select
            value={value?.name || ''}
            onChange={(e) => onChange({ source: 'page_input', name: e.target.value })}
            style={{ ...INPUT, flex: 1 }}
          >
            <option value="">— Pick variable —</option>
            {value?.name && !inputs.some((i) => i.name === value.name) && (
              <option value={value.name}>{value.name} (not declared on this page)</option>
            )}
            {inputs.map((inp) => (
              <option key={inp.name} value={inp.name}>{inp.label || inp.name}</option>
            ))}
          </select>
        ) : (
          <input
            value={value?.value || ''}
            onChange={(e) => onChange({ source: 'static', value: e.target.value })}
            placeholder="Value"
            style={{ ...INPUT, flex: 1 }}
          />
        )}
      </div>
    </div>
  )
}

export function FileUploadEditor({
  block, page, onChange,
}: {
  block: FileUploadBlock
  page: PageModel
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<FileUploadBlock>) => onChange({ ...block, ...patch })
  const inputs = page.inputs || []
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Caption</label>
        <input
          value={block.label || ''}
          onChange={(e) => upd({ label: e.target.value })}
          style={INPUT}
          placeholder="Attach a file"
        />
      </div>
      <div>
        <label style={FIELD_LABEL}>Upload kind</label>
        <select
          value={block.kind || 'document'}
          onChange={(e) => upd({ kind: e.target.value as 'image' | 'document' })}
          style={INPUT}
        >
          <option value="document">Document (any file)</option>
          <option value="image">Image</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>Inline preview</label>
        <select
          value={block.previewMode || 'auto'}
          onChange={(e) => upd({ previewMode: e.target.value as FileUploadBlock['previewMode'] })}
          style={INPUT}
        >
          <option value="auto">Auto (detect from file)</option>
          <option value="image">Image viewer</option>
          <option value="pdf">PDF viewer</option>
          <option value="text">Text viewer</option>
          <option value="none">None (chip only)</option>
        </select>
      </div>
      <FilterValueRefControl
        label="Document type"
        value={block.docType}
        inputs={inputs}
        onChange={(docType) => upd({ docType })}
      />
      <FilterValueRefControl
        label="Transaction id"
        value={block.transactionId}
        inputs={inputs}
        onChange={(transactionId) => upd({ transactionId })}
      />
      <div>
        <label style={FIELD_LABEL}>Store URL in page variable</label>
        <select
          value={block.valueVar || ''}
          onChange={(e) => upd({ valueVar: e.target.value || undefined })}
          style={INPUT}
        >
          <option value="">— None —</option>
          {block.valueVar && !inputs.some((i) => i.name === block.valueVar) && (
            <option value={block.valueVar}>{block.valueVar} (not declared on this page)</option>
          )}
          {inputs.map((inp) => (
            <option key={inp.name} value={inp.name}>{inp.label || inp.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL}>On upload — run action</label>
        <select
          value={block.onUploadAction || ''}
          onChange={(e) => upd({ onUploadAction: e.target.value || undefined })}
          style={INPUT}
        >
          <option value="">— None —</option>
          {block.onUploadAction && !(page.actions || []).some((a) => a.name === block.onUploadAction) && (
            <option value={block.onUploadAction}>{block.onUploadAction} (not defined)</option>
          )}
          {(page.actions || []).map((a) => (
            <option key={a.id} value={a.name}>{a.name}</option>
          ))}
        </select>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Runs after a successful upload. Use a Call API step that reads the uploaded
          URL ({block.valueVar ? `{{${block.valueVar}}}` : 'set "Store URL in" first'}) and
          saves the JSON response to a variable (the step's "Save response object to variable").
        </p>
      </div>
      <div>
        <label style={FIELD_LABEL}>Accept (optional override)</label>
        <input
          value={block.accept || ''}
          onChange={(e) => upd({ accept: e.target.value || undefined })}
          style={{ ...INPUT, fontFamily: 'monospace' }}
          placeholder={block.kind === 'image' ? 'image/*' : '.pdf,.docx,…'}
        />
      </div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '2px 0 0' }}>
        The file is stored under <code>{'<document type>/<transaction id>/'}</code> in the
        project document store — grouped by type, then by record, for easy archive/backup.
      </p>
    </>
  )
}

// ─── Authentication menu ─────────────────────────────
// Configures the header `auth_menu` widget: signed-out button labels, whether
// to offer Sign up, the cluster alignment, the self-service toggles (profile,
// change password, avatar upload), and any extra links shown in the signed-in
// dropdown.
export function AuthMenuEditor({ block, onChange }: { block: AuthMenuBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<AuthMenuBlock>) => onChange({ ...block, ...patch })
  const links: AuthMenuLink[] = block.links || []
  const setLink = (i: number, patch: Partial<AuthMenuLink>) =>
    upd({ links: links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) })
  const removeLink = (i: number) => upd({ links: links.filter((_, idx) => idx !== i) })
  const addLink = () =>
    upd({ links: [...links, { id: newId('lnk'), label: 'New link', href: '/' }] })

  // Each toggle defaults to ON (checked unless explicitly set to false).
  const check = (key: keyof AuthMenuBlock, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="checkbox" id={`am-${String(key)}-${block.id}`}
        checked={(block[key] as boolean | undefined) !== false}
        onChange={(e) => upd({ [key]: e.target.checked } as Partial<AuthMenuBlock>)} />
      <label htmlFor={`am-${String(key)}-${block.id}`} style={{ fontSize: '0.9125rem' }}>{label}</label>
    </div>
  )

  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Log in button label</label>
        <input value={block.loginLabel || ''} onChange={(e) => upd({ loginLabel: e.target.value })} style={INPUT} placeholder="Log in" />
      </div>
      {check('showSignup', 'Show "Sign up" button when signed out')}
      {block.showSignup !== false && (
        <div>
          <label style={FIELD_LABEL}>Sign up button label</label>
          <input value={block.signupLabel || ''} onChange={(e) => upd({ signupLabel: e.target.value })} style={INPUT} placeholder="Sign up" />
        </div>
      )}
      <div>
        <label style={FIELD_LABEL}>Alignment</label>
        <select value={block.align || 'right'} onChange={(e) => upd({ align: e.target.value as Align })} style={INPUT}>
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {check('showProfile', 'Show "My account" in the user menu')}
        {check('showChangePassword', 'Allow changing password')}
        {check('allowAvatarUpload', 'Allow uploading a profile image')}
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
        <label style={FIELD_LABEL}>Extra menu links (shown when signed in)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {links.map((l, i) => (
            <div key={l.id || i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 70px auto', gap: 6, alignItems: 'center' }}>
              <input value={l.label} onChange={(e) => setLink(i, { label: e.target.value })} style={INPUT} placeholder="Label" />
              <input value={l.href || ''} onChange={(e) => setLink(i, { href: e.target.value })} style={INPUT} placeholder="/dashboard or https://…" />
              <input value={l.icon || ''} onChange={(e) => setLink(i, { icon: e.target.value || undefined })} style={INPUT}
                placeholder="icon" title="Material icon name, e.g. dashboard" />
              <button type="button" onClick={() => removeLink(i)} title="Remove"
                style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', padding: 6, color: 'var(--color-text-secondary)', display: 'flex' }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addLink}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', padding: '6px 10px', fontSize: '0.8625rem', fontWeight: 600, background: 'none', border: '1px dashed var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text)' }}>
            <Plus size={14} /> Add link
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Cart (client-side storefront) ───────────────────
// The three cart blocks run with no session — the cart lives in the
// visitor's browser — so everything here is presentation: labels, links and
// the DECLARED tax/shipping rules a static page computes totals from.
// Placeholders are the runtime's own defaults (renderers.tsx).

function optText(v: string): string | undefined { return v === '' ? undefined : v }
function optNumber(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

export function CartBadgeEditor({ block, onChange }: { block: CartBadgeBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CartBadgeBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Label</label>
        <input value={block.label || ''} placeholder="Cart" onChange={(e) => upd({ label: optText(e.target.value) })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Links to</label>
        <input value={block.href || ''} placeholder="/cart" onChange={(e) => upd({ href: optText(e.target.value) })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Icon</label>
        <IconField value={block.icon} placeholder="shopping-cart" onChange={(icon) => upd({ icon })} />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={!!block.iconOnly}
               onChange={(e) => upd({ iconOnly: e.target.checked || undefined })} />
        Icon only (count on the icon; the label is read out, not shown)
      </label>
      <div><label style={FIELD_LABEL}>Style</label>
        <select value={block.variant || 'primary'} onChange={(e) => upd({ variant: e.target.value as ButtonVariant })} style={INPUT}>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="outline">Outline</option>
          <option value="ghost">Ghost</option>
          <option value="link">Link</option>
        </select>
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={block.hideWhenEmpty !== false}
               onChange={(e) => upd({ hideWhenEmpty: e.target.checked })} />
        Hide the count while the cart is empty
      </label>
    </>
  )
}

export function CartLinesEditor({ block, onChange }: { block: CartLinesBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CartLinesBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={block.editable !== false}
               onChange={(e) => upd({ editable: e.target.checked })} />
        Show the quantity stepper and remove button
      </label>
      <div><label style={FIELD_LABEL}>Empty cart message</label>
        <textarea value={block.emptyText || ''} rows={2} placeholder="Your cart is empty."
                  onChange={(e) => upd({ emptyText: optText(e.target.value) })}
                  style={{ ...INPUT, resize: 'vertical' }} />
      </div>
      <div><label style={FIELD_LABEL}>Continue-shopping link</label>
        <div style={TWO_COL}>
          <input value={block.continueLabel || ''} placeholder="Continue browsing"
                 onChange={(e) => upd({ continueLabel: optText(e.target.value) })} style={INPUT} />
          <input value={block.continueHref || ''} placeholder="/shop"
                 onChange={(e) => upd({ continueHref: optText(e.target.value) })} style={INPUT} />
        </div>
        <p style={HINT}>Shown under the empty-cart message only when a link is set.</p>
      </div>
    </>
  )
}

export function CartSummaryEditor({ block, onChange }: { block: CartSummaryBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CartSummaryBlock>) => onChange({ ...block, ...patch })
  const text = (key: keyof CartSummaryBlock, label: string, placeholder: string) => (
    <div><label style={FIELD_LABEL}>{label}</label>
      <input value={(block[key] as string | undefined) || ''} placeholder={placeholder}
             onChange={(e) => upd({ [key]: optText(e.target.value) } as Partial<CartSummaryBlock>)} style={INPUT} />
    </div>
  )
  const num = (key: 'taxPercent' | 'shippingFlat' | 'shippingFreeOver', label: string) => (
    <div><label style={FIELD_LABEL}>{label}</label>
      <input type="number" min={0} step="any" value={block[key] ?? ''} placeholder="—"
             onChange={(e) => upd({ [key]: optNumber(e.target.value) })} style={INPUT} />
    </div>
  )
  return (
    <>
      {text('heading', 'Heading', 'None')}
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Charges</label>
        <div style={TWO_COL}>
          {num('taxPercent', 'Tax %')}
          {num('shippingFlat', 'Flat shipping')}
        </div>
        {num('shippingFreeOver', 'Free shipping over')}
        <p style={HINT}>
          Declared here, not looked up — a static page has no server to ask.
          A row appears only when its charge is set.
        </p>
      </div>
      <div style={SUBSECTION}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Row labels</label>
        {text('subtotalLabel', 'Subtotal', 'Subtotal')}
        {text('taxLabel', 'Tax', block.taxPercent ? `Tax at ${block.taxPercent}%` : 'Tax at N%')}
        {text('shippingLabel', 'Shipping', 'Shipping')}
        {text('freeLabel', 'Free shipping', 'Free')}
        {text('totalLabel', 'Total', 'Total')}
      </div>
      <div><label style={FIELD_LABEL}>Checkout button</label>
        <div style={TWO_COL}>
          <input value={block.checkoutLabel || ''} placeholder="Proceed to checkout"
                 onChange={(e) => upd({ checkoutLabel: optText(e.target.value) })} style={INPUT} />
          <input value={block.checkoutHref || ''} placeholder="/checkout"
                 onChange={(e) => upd({ checkoutHref: optText(e.target.value) })} style={INPUT} />
        </div>
        <p style={HINT}>The button appears only when a link is set.</p>
      </div>
      <div><label style={FIELD_LABEL}>Note</label>
        <textarea value={block.note || ''} rows={2}
                  onChange={(e) => upd({ note: optText(e.target.value) })}
                  style={{ ...INPUT, resize: 'vertical' }} />
      </div>
    </>
  )
}

// ─── Wishlist (client-side "shopping list") ──────────
// Like the cart: kept in the visitor's browser, so these are presentation
// only. Placeholders are the runtime's defaults (renderers.tsx).

function VariantSelect({ value, fallback, onChange }: { value?: ButtonVariant; fallback: ButtonVariant; onChange: (v: ButtonVariant) => void }) {
  return (
    <select value={value || fallback} onChange={(e) => onChange(e.target.value as ButtonVariant)} style={INPUT}>
      <option value="primary">Primary</option>
      <option value="secondary">Secondary</option>
      <option value="outline">Outline</option>
      <option value="ghost">Ghost</option>
      <option value="link">Link</option>
    </select>
  )
}

export function WishlistButtonEditor({ block, onChange }: { block: WishlistButtonBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<WishlistButtonBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Item code (SKU)</label>
        <input value={block.sku || ''} placeholder="supports {{vars}} — one entry per code"
               onChange={(e) => upd({ sku: e.target.value })} style={{ ...INPUT, fontFamily: 'monospace' }} />
      </div>
      <div><label style={FIELD_LABEL}>Name</label>
        <input value={block.name || ''} placeholder="What the wishlist calls it"
               onChange={(e) => upd({ name: e.target.value })} style={INPUT} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Price</label>
          <input value={block.price || ''} placeholder="₹3,200" onChange={(e) => upd({ price: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Second line</label>
          <input value={block.meta || ''} onChange={(e) => upd({ meta: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Links back to</label>
        <input value={block.href || ''} placeholder="/product-page" onChange={(e) => upd({ href: optText(e.target.value) })} style={{ ...INPUT, fontFamily: 'monospace' }} />
      </div>
      <div><label style={FIELD_LABEL}>Image URL</label>
        <input value={block.image || ''} onChange={(e) => upd({ image: optText(e.target.value) })} style={{ ...INPUT, fontFamily: 'monospace' }} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Label (not saved)</label>
          <input value={block.addLabel || ''} placeholder="Add to wishlist" onChange={(e) => upd({ addLabel: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Label (saved)</label>
          <input value={block.removeLabel || ''} placeholder="Saved to wishlist" onChange={(e) => upd({ removeLabel: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Display</label>
          <select value={block.display || 'button'} onChange={(e) => upd({ display: e.target.value as 'icon' | 'button' })} style={INPUT}>
            <option value="button">Heart + label</option>
            <option value="icon">Heart only</option>
          </select>
        </div>
        <div><label style={FIELD_LABEL}>Style</label>
          <VariantSelect value={block.variant} fallback="outline" onChange={(variant) => upd({ variant })} />
        </div>
      </div>
    </>
  )
}

export function WishlistBadgeEditor({ block, onChange }: { block: WishlistBadgeBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<WishlistBadgeBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Label</label>
        <input value={block.label ?? ''} placeholder="Wishlist" onChange={(e) => upd({ label: optText(e.target.value) })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Links to</label>
        <input value={block.href || ''} placeholder="/wishlist" onChange={(e) => upd({ href: optText(e.target.value) })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Icon</label>
        <IconField value={block.icon} placeholder="heart" onChange={(icon) => upd({ icon })} />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={!!block.iconOnly}
               onChange={(e) => upd({ iconOnly: e.target.checked || undefined })} />
        Icon only (count on the icon; the label is read out, not shown)
      </label>
      <div><label style={FIELD_LABEL}>Style</label>
        <VariantSelect value={block.variant} fallback="outline" onChange={(variant) => upd({ variant })} />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={block.hideWhenEmpty !== false}
               onChange={(e) => upd({ hideWhenEmpty: e.target.checked })} />
        Hide the count while the wishlist is empty
      </label>
    </>
  )
}

export function WishlistLinesEditor({ block, onChange }: { block: WishlistLinesBlock; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<WishlistLinesBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={block.showMove !== false}
               onChange={(e) => upd({ showMove: e.target.checked })} />
        Offer "Move to cart" on each item
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={!!block.keepAfterMove}
               onChange={(e) => upd({ keepAfterMove: e.target.checked || undefined })} />
        Keep the item on the list after moving it to the cart
      </label>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Move label</label>
          <input value={block.moveLabel || ''} placeholder="Move to cart" onChange={(e) => upd({ moveLabel: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Remove label</label>
          <input value={block.removeLabel || ''} placeholder="Remove" onChange={(e) => upd({ removeLabel: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Empty message</label>
        <textarea value={block.emptyText || ''} rows={2} placeholder="Your wishlist is empty."
                  onChange={(e) => upd({ emptyText: optText(e.target.value) })}
                  style={{ ...INPUT, resize: 'vertical' }} />
      </div>
      <div><label style={FIELD_LABEL}>Continue-shopping link</label>
        <div style={TWO_COL}>
          <input value={block.continueLabel || ''} placeholder="Continue browsing"
                 onChange={(e) => upd({ continueLabel: optText(e.target.value) })} style={INPUT} />
          <input value={block.continueHref || ''} placeholder="/shop"
                 onChange={(e) => upd({ continueHref: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
    </>
  )
}
